import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    BookOpen, Bot, Building2, Check, Copy, Download, Film, Flame, Image as ImageIcon, KeyRound, Lightbulb, Loader2, Plus,
    BarChart3, Bell, BellRing, EyeOff, FileText, RefreshCw, RotateCcw, Save, Search, Settings, Sparkles, Trash2, Trophy, Upload, Wand2, X
} from 'lucide-react';
import { FB_GUIDE } from './fbGuide';
import FbLogo from './FbLogo';
import { addManifest, callAssist, clearPendingAssist, enablePush, ensureWorker, notifyState, resumeAssist, showLocal, takePendingAssist } from './fbBackground';

// owner of the admin's own pages (same id as server/fb.js ADMIN_OWNER)
const ADMIN_OWNER = 'admin';
// BM pages the admin removed from the list (server/fb.js HIDDEN_OWNER)
const HIDDEN_OWNER = 'hidden';
// pages whose BM access was removed: the sync keeps failing, so the admin can filter them out and delete them for good
const BROKEN = 'broken';
const isBroken = p => p.bm && !!p.syncError;
const CM_ON = 'cm';
const cmMoney = cm => (cm?.total28 ? `${cm.total28.toLocaleString('vi-VN')} ${cm.currency || ''}`.trim() : '');
const TABS = [
    { id: 'create', label: 'Tạo nội dung', icon: Sparkles },
    { id: 'library', label: 'Thư viện', icon: ImageIcon },
    { id: 'prompts', label: 'Prompt của tôi', icon: FileText },
    { id: 'pages', label: 'Page của tôi', icon: BookOpen },
    { id: 'trends', label: 'Trend hot', icon: Flame },
    { id: 'insight', label: 'Insight', icon: BarChart3 },
    { id: 'board', label: 'Bảng vàng', icon: Trophy },
    { id: 'guide', label: 'Hướng dẫn', icon: Lightbulb },
    { id: 'admin', label: 'Quản trị', icon: Settings, admin: true }
];
const PAGE_STATUS = { building: 'Đang xây', growing: 'Đang lên', monetized: 'Đã bật kiếm tiền', paused: 'Tạm dừng' };
const KIND_LABEL = { images: 'Ảnh', videos: 'Video', ai: 'Lượt AI' };
const fmt = n => Number(n || 0).toLocaleString('vi-VN');
const clock = ts => (ts ? new Date(ts).toLocaleString('vi-VN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' }) : '');
const today = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Ho_Chi_Minh' }).format(new Date());

const card = 'rounded-xl border border-slate-700/70 bg-slate-900/70 p-4';
const input = 'w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:border-amber-400 focus:outline-none';
const btn = 'inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50';
const btnMain = `${btn} bg-amber-400 text-slate-950 hover:bg-amber-300`;
const btnGhost = `${btn} border border-slate-700 text-slate-200 hover:bg-slate-800`;
const label = 'mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-400';

function useApi(firebaseUser) {
    return useCallback(async (action, body) => {
        if (!firebaseUser) throw new Error('Đang khôi phục phiên đăng nhập, thử lại sau vài giây.');
        const send = async force => {
            const token = await firebaseUser.getIdToken(force);
            return fetch(`/api/fb?action=${action}`, {
                method: body ? 'POST' : 'GET',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: body ? JSON.stringify(body) : undefined
            });
        };
        let response = await send(false);
        if (response.status === 401) response = await send(true);
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.error || `HTTP ${response.status}`);
        return payload;
    }, [firebaseUser]);
}

function CopyButton({ text, small }) {
    const [done, setDone] = useState(false);
    return (
        <button type="button" className={small ? 'rounded p-1 text-slate-400 hover:bg-slate-800 hover:text-white' : btnGhost} title="Sao chép"
            onClick={() => navigator.clipboard.writeText(text).then(() => { setDone(true); setTimeout(() => setDone(false), 1200); })}>
            {done ? <Check size={14} /> : <Copy size={14} />}{!small && (done ? 'Đã chép' : 'Chép')}
        </button>
    );
}

function Notice({ tone = 'info', children, onClose }) {
    const tones = { info: 'border-sky-500/30 bg-sky-500/10 text-sky-100', warn: 'border-amber-500/40 bg-amber-500/10 text-amber-100', error: 'border-red-500/40 bg-red-500/10 text-red-100', ok: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-100' };
    return (
        <div className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${tones[tone]}`}>
            <div className="flex-1">{children}</div>
            {onClose && <button type="button" onClick={onClose} className="opacity-70 hover:opacity-100"><X size={14} /></button>}
        </div>
    );
}

/** Shrinks a picked photo to a JPEG the API accepts (one upload ≤ ~600 KB). */
async function fileToRef(file) {
    const url = URL.createObjectURL(file);
    try {
        const img = await new Promise((resolve, reject) => { const i = new Image(); i.onload = () => resolve(i); i.onerror = reject; i.src = url; });
        for (const max of [1280, 1024, 800]) {
            const scale = Math.min(1, max / Math.max(img.width, img.height));
            const canvas = document.createElement('canvas');
            canvas.width = Math.round(img.width * scale);
            canvas.height = Math.round(img.height * scale);
            canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
            const data = canvas.toDataURL('image/jpeg', 0.85).split(',')[1];
            if (data.length <= 790000) return { data, file_name: file.name.replace(/\.[^.]+$/, '') + '.jpg' };
        }
        throw new Error('Ảnh quá lớn, chọn ảnh khác.');
    } finally {
        URL.revokeObjectURL(url);
    }
}

function Media({ g, className = '' }) {
    if (g.state !== 'done' || !g.urls?.[0]) return null;
    return g.kind === 'video'
        ? <video src={g.urls[0]} controls playsInline preload="metadata" className={`w-full rounded-lg bg-black ${className}`} />
        : <img src={g.urls[0]} alt={g.title || 'Ảnh AI'} loading="lazy" className={`w-full rounded-lg bg-black object-contain ${className}`} />;
}

const timeLeft = etaAt => {
    const min = Math.round((etaAt - Date.now()) / 60000);
    return min <= 1 ? 'khoảng 1 phút' : `~${min} phút`;
};

const LONG_RUN_MS = 40 * 60 * 1000;
const minutesSince = t => Math.max(1, Math.round((Date.now() - t) / 60000));
const isRunning = g => g.state === 'pending' || g.state === 'queued';

function GenStatus({ g }) {
    if (g.state === 'queued') {
        return (
            <span className="inline-flex items-center gap-1 text-xs text-sky-300" title="Mỗi người chạy tối đa 2 video/ảnh cùng lúc; job này tự chạy khi có slot trống.">
                <Loader2 size={12} className="animate-spin" /> Xếp hàng{g.position ? ` #${g.position}` : ''}{g.etaAt ? ` · xong sau ${timeLeft(g.etaAt)}` : '…'}
            </span>
        );
    }
    if (g.state === 'pending' && Date.now() - (g.startedAt || g.createdAt) > LONG_RUN_MS) {
        return (
            <span className="inline-flex items-center gap-1 text-xs text-amber-300" title="Máy tạo đã nhận job và vẫn đang làm. Bấm tạo lại sẽ ra thêm một bản giống hệt và tốn thêm lượt.">
                <Loader2 size={12} className="animate-spin" /> Đang tạo {minutesSince(g.startedAt || g.createdAt)} phút — lâu hơn thường lệ, đừng bấm tạo lại
            </span>
        );
    }
    if (g.state === 'pending') {
        return (
            <span className="inline-flex items-center gap-1 text-xs text-amber-300" title="Ước tính theo thời gian tạo thực tế của các job gần đây.">
                <Loader2 size={12} className="animate-spin" /> {g.slow ? `Máy chủ đang chậm hơn thường lệ — chờ thêm ${timeLeft(g.etaAt)}` : `Đang tạo${g.etaAt ? ` · còn ${timeLeft(g.etaAt)}` : '…'}`}
            </span>
        );
    }
    if (g.state === 'failed') return <span className="text-xs text-red-300">Lỗi (đã hoàn lượt): {g.error}</span>;
    return <span className="text-xs text-emerald-300">Xong</span>;
}

// ---------------- QA thủ công ----------------

const QA_ISSUES = ['Chủ thể biến dạng', 'Chuyển động lỗi/giật', 'Kết thúc dở dang', 'Thiếu điểm WOW', 'Có chữ/logo lạ', 'Lệch ngách page', 'Màu/ánh sáng xấu'];
const QA_CHECK = 'Xem hết clip rồi kiểm: chủ thể rõ, không biến dạng · chuyển động mượt · kết thúc trọn vẹn · có điểm WOW · không chữ/logo lạ · đúng ngách page.';
const qaFeedback = g => [...(g.qa?.issues || []), g.qa?.note].filter(Boolean).join('; ');
/** Draft fields to reuse a result's prompt; the idea is filled too when empty, since the prompt step opens only after one. */
const reusePatch = (g, d) => ({
    ...(g.kind === 'video' ? { videoPrompt: g.prompt, seconds: g.seconds || d.seconds } : { imagePrompt: g.prompt }),
    title: g.title || d.title,
    pageId: g.pageId || d.pageId,
    idea: d.idea.trim().length >= 10 ? d.idea : `Làm lại: ${g.title || g.prompt.slice(0, 120)}`.padEnd(10, '.')
});
/** …plus what failed QA, ready for "AI tối ưu". */
const fixPatch = (g, d) => ({ ...reusePatch(g, d), feedback: qaFeedback(g) });

/** Staff check every result by hand before posting: passed, or failed with what went wrong (sent back to the prompt). */
function QaBox({ g, api, onSaved, onFix }) {
    const [open, setOpen] = useState(false);
    const [issues, setIssues] = useState([]);
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    if (g.state !== 'done') return null;
    const save = async (state, extra = {}) => {
        setBusy(true);
        try {
            onSaved(await api('generation-qa', { id: g.id, state, ...extra }));
            setOpen(false);
        } catch (e) {
            window.alert(e.message);
        } finally {
            setBusy(false);
        }
    };
    if (g.qa?.state === 'ok') {
        return (
            <div className="flex items-center justify-between gap-2 rounded-lg bg-emerald-500/10 px-2 py-1.5 text-xs text-emerald-300">
                <span>✅ Đạt QA{g.posted ? ' · đã đăng' : ' — tải về rồi đăng lên page'}</span>
                <button type="button" className="text-slate-400 hover:text-white" disabled={busy} onClick={() => save('')}>Đổi</button>
            </div>
        );
    }
    if (g.qa?.state === 'fail') {
        return (
            <div className="space-y-1 rounded-lg bg-red-500/10 px-2 py-1.5 text-xs text-red-200">
                <div>❌ Chưa đạt: {qaFeedback(g) || 'không ghi lý do'}</div>
                <div className="flex gap-3">
                    <button type="button" className="font-semibold text-amber-300 hover:underline" onClick={() => onFix(g)}>Sửa prompt & tạo lại</button>
                    <button type="button" className="text-slate-400 hover:text-white" disabled={busy} onClick={() => save('')}>Đổi</button>
                </div>
            </div>
        );
    }
    return (
        <div className="space-y-2 rounded-lg border border-slate-700 p-2">
            <p className="text-[11px] leading-snug text-slate-400">{QA_CHECK}</p>
            <div className="flex gap-2">
                <button type="button" className={`${btn} flex-1 bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30`} disabled={busy} onClick={() => save('ok')}><Check size={14} /> Đạt</button>
                <button type="button" className={`${btn} flex-1 bg-red-500/15 text-red-200 hover:bg-red-500/25`} disabled={busy} onClick={() => setOpen(o => !o)}><X size={14} /> Chưa đạt</button>
            </div>
            {open && (
                <div className="space-y-2">
                    <div className="flex flex-wrap gap-1.5">
                        {QA_ISSUES.map(x => (
                            <button key={x} type="button" onClick={() => setIssues(l => (l.includes(x) ? l.filter(y => y !== x) : [...l, x]))}
                                className={`rounded-full px-2 py-1 text-xs ${issues.includes(x) ? 'bg-red-400 text-slate-950' : 'bg-slate-800 text-slate-200'}`}>{x}</button>
                        ))}
                    </div>
                    <input className={input} value={note} onChange={e => setNote(e.target.value)} placeholder="Ghi rõ lỗi (VD: con chim mất chân ở giây 8)" />
                    <button type="button" className={`${btnGhost} w-full`} disabled={busy || (!issues.length && !note.trim())} onClick={() => save('fail', { issues, note })}>Lưu kết quả QA</button>
                </div>
            )}
        </div>
    );
}

/** Running jobs and fresh results still waiting for QA, on top of the page — on a phone the results column is far below. */
function JobStrip({ gens, onOpen }) {
    const since = Date.now() - 6 * 3600 * 1000;
    const list = gens.filter(g => g.state === 'pending' || g.state === 'queued' || (g.state === 'done' && !g.qa && g.createdAt > since)).slice(0, 5);
    if (!list.length) return null;
    return (
        <div className={`${card} space-y-1.5 p-3`}>
            <div className="flex items-center justify-between gap-2">
                <strong className="text-sm text-white">Đang tạo & chờ QA</strong>
                <button type="button" className="text-xs font-semibold text-amber-300 hover:underline" onClick={onOpen}>Mở thư viện →</button>
            </div>
            {list.map(g => (
                <div key={g.id} className="flex items-center justify-between gap-2 text-sm">
                    <span className="min-w-0 truncate text-slate-200">{g.kind === 'video' ? '🎬' : '🖼️'} {g.title || g.prompt.slice(0, 40)}</span>
                    {g.state === 'done'
                        ? <button type="button" onClick={onOpen} className="shrink-0 text-xs font-semibold text-emerald-300">✅ Xong · QA & tải</button>
                        : <span className="shrink-0"><GenStatus g={g} /></span>}
                </div>
            ))}
        </div>
    );
}

/** Always-visible bar at the bottom while anything is being made, so a phone user knows the tool is still working. */
function RunningBar({ gens, onOpen }) {
    const running = gens.filter(isRunning);
    if (!running.length) return null;
    const g = running.find(x => x.state === 'pending') || running[0];
    return (
        <button type="button" onClick={onOpen}
            className="fixed inset-x-2 bottom-2 z-40 flex items-center gap-3 rounded-xl border border-amber-400/50 bg-slate-950/95 px-4 py-3 text-left shadow-2xl backdrop-blur sm:left-auto sm:right-4 sm:w-96">
            <Loader2 size={22} className="shrink-0 animate-spin text-amber-300" />
            <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-white">
                    {g.state === 'queued' ? 'Đang chờ tạo' : 'Đang tạo'} {g.kind === 'video' ? 'video' : 'ảnh'}{g.title ? ` "${g.title}"` : ''}
                </span>
                <span className="block"><GenStatus g={g} /></span>
                {running.length > 1 && <span className="block text-xs text-slate-400">+{running.length - 1} việc khác đang chạy/chờ</span>}
            </span>
            <span className="shrink-0 text-xs font-semibold text-amber-300">Xem →</span>
        </button>
    );
}

// ---------------- Tạo nội dung ----------------

/** One step of the create flow; locked steps show only their title until the previous step is done. */
function Step({ n, title, isOpen, isDone, refs, children }) {
    return (
        <section ref={el => { refs.current[n - 1] = el; }} className={`${card} scroll-mt-14 ${isOpen ? '' : 'opacity-60'}`}>
            <h3 className={`${isOpen ? 'mb-3' : ''} flex items-center gap-2 font-bold text-white`}>
                <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-xs ${isDone ? 'bg-emerald-400 text-slate-950' : isOpen ? 'bg-amber-400 text-slate-950' : 'bg-slate-700 text-slate-300'}`}>{isDone ? <Check size={13} /> : n}</span>
                {title}
                {!isOpen && <span className="ml-auto text-xs font-normal text-slate-500">Xong bước {n - 1} để mở</span>}
            </h3>
            {isOpen && children}
        </section>
    );
}

const TIER_TONE = { hot: 'bg-rose-500/15 text-rose-300', good: 'bg-emerald-500/15 text-emerald-300', build: 'bg-sky-500/15 text-sky-300', weak: 'bg-amber-500/15 text-amber-300', steady: 'bg-slate-700 text-slate-300', paused: 'bg-slate-800 text-slate-500' };

/** Today's plan per page: how many videos, which base prompt, which winning post to riff on; one click fills step 1. */
const IDEA_KIND = {
    exploit: { label: '🔁 Làm tiếp cái thắng', tone: 'text-emerald-300' },
    explore: { label: '🧪 Thử mới', tone: 'text-sky-300' },
    cross: { label: '🔀 Mượn bài thắng page bạn', tone: 'text-fuchsia-300' }
};

function TodayPlan({ pages, masters, current, onTake }) {
    const nameOf = id => masters.find(m => m.id === id)?.name || '';
    const total = pages.reduce((s, p) => s + p.plan.target, 0);
    return (
        <div className="mb-4 rounded-lg border border-amber-400/30 bg-amber-400/5 p-3">
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                <strong className="text-sm text-amber-200">Kế hoạch hôm nay · {total} video</strong>
                <span className="text-[11px] text-slate-400">Tính theo view 7 ngày của từng page. Bấm "Làm bài này" để điền sẵn bước 1.</span>
            </div>
            <div className="space-y-2">
                {pages.map(p => (
                    <div key={p.id} className={`rounded-md p-2 ${current === p.id ? 'bg-slate-900 ring-1 ring-amber-400/50' : 'bg-slate-950/60'}`}>
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                            <b className="text-white">{p.name}</b>
                            <span className={`rounded px-1.5 py-0.5 text-[11px] ${TIER_TONE[p.plan.tier] || TIER_TONE.steady}`}>{p.plan.label}</span>
                            <span className="text-xs text-slate-400">{p.plan.target} video/ngày · đã dùng {p.plan.made.videos}/{p.plan.cap} lượt tạo video</span>
                        </div>
                        <p className="text-[11px] text-slate-500">{p.plan.why}</p>
                        {p.plan.target > 0 && p.plan.hours?.hours?.length > 0 && (
                            <p className="text-[11px] text-slate-400" title="Giờ Việt Nam, tính từ giờ đăng và view các bài trên BM">
                                🕒 Giờ đăng tốt: <b className="text-slate-200">{p.plan.hours.hours.map(h => `${h}h`).join(' · ')}</b>{p.plan.hours.own ? '' : ' (theo dữ liệu cả team, page còn ít bài)'}
                            </p>
                        )}
                        {p.plan.ideas.map((idea, i) => (
                            <div key={i} className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                                <span className={IDEA_KIND[idea.kind]?.tone || 'text-emerald-300'}>{IDEA_KIND[idea.kind]?.label || IDEA_KIND.exploit.label}</span>
                                <span className="text-slate-300">{nameOf(idea.masterId) || 'Chưa có prompt nền'}</span>
                                {idea.ref && <span className="text-slate-500">· {idea.kind === 'cross' ? `bài của page "${idea.ref.pageName}"` : 'giống bài'} "{idea.ref.title.slice(0, 50)}" ({fmt(idea.ref.views)} view)</span>}
                                <button type="button" className="ml-auto rounded bg-amber-400/20 px-2 py-0.5 font-semibold text-amber-200 hover:bg-amber-400/30" disabled={p.plan.made.videos >= p.plan.cap} onClick={() => onTake(p, idea)}>Làm bài này</button>
                            </div>
                        ))}
                        {!p.plan.target && <p className="mt-1 text-xs text-slate-500">Hôm nay không tạo video cho page này.</p>}
                    </div>
                ))}
            </div>
        </div>
    );
}

function CreateTab({ api, me, gens, setGens, refreshMe, draft, setDraft, onLibrary }) {
    const [busy, setBusy] = useState('');
    const [error, setError] = useState('');
    const [ideas, setIdeas] = useState([]);
    const [notesVi, setNotesVi] = useState('');
    const [changes, setChanges] = useState([]);
    const [captions, setCaptions] = useState(null);
    const [upload, setUpload] = useState(null);
    const fileRef = useRef(null);
    const videoRef = useRef(null);
    const set = patch => setDraft(d => ({ ...d, ...patch }));
    const doneImages = gens.filter(g => g.kind === 'image' && g.state === 'done');
    const session = gens.filter(g => g.createdAt >= (draft.since || 0));
    // a new image in this session replaces the previous one (older ones stay in the library)
    const sessionImages = session.filter(g => g.kind === 'image' && g.state !== 'failed');
    const latestImage = sessionImages[0] || null;
    const latest = session.filter(g => g.kind !== 'image' || g.state === 'failed' || g === latestImage).slice(0, 6);
    const myPages = me.myPages || [];
    const page = myPages.find(p => p.id === draft.pageId);
    // The page's own AI-picked masters come first; without a page, masters whose topic matches the staff's niches
    const pageMasters = (page?.masterIds || []).map(id => me.masters.find(m => m.id === id)).filter(Boolean);
    const fitting = pageMasters.length ? pageMasters : me.masters.filter(m => m.fit > 0).sort((a, b) => b.fit - a.fit).slice(0, 8);
    const masterGroups = groupMasters(me.masters);
    const chosenMaster = me.masters.find(m => m.id === draft.masterId);
    // Staff work from their page's prompts only (the admin picks them in Quản trị → Page của team), so nobody grabs a
    // prompt from another niche out of the 80+ in the library; a page without picks falls back to the whole library
    const lockedTo = me.role !== 'admin' && pageMasters.length > 0;
    const mustPickPage = me.role !== 'admin' && myPages.length > 0 && !page;
    const pickPage = id => {
        const p = myPages.find(x => x.id === id);
        const first = p?.masterIds.find(mid => me.masters.some(m => m.id === mid));
        set({ pageId: id, ...(p?.niche ? { niche: p.niche } : {}), ...(first ? { masterId: first } : {}) });
    };
    // "Làm bài này" from today's plan: page, base prompt and a note for the AI in one click
    const takeIdea = (p, idea) => {
        const known = me.masters.some(m => m.id === idea.masterId);
        set({ pageId: p.id, ...(p.niche ? { niche: p.niche } : {}), ...(known ? { masterId: idea.masterId } : {}), notes: idea.note });
        setIdeas([]);
    };
    // Every image/video is charged to a page and its plan for today (the admin may test without one)
    const needPage = me.role !== 'admin';
    const pageLeft = kind => (page ? Math.max(0, (kind === 'videos' ? page.plan.cap : page.plan.imageCap) - page.plan.made[kind]) : needPage ? 0 : Infinity);
    // GenCPU unlimited: videos only need a page that has a plan today, no daily count
    const freeVideo = kind => kind === 'videos' && me.options.gencpuFree;
    const canMake = kind => (freeVideo(kind) ? (page ? page.plan.cap > 0 : !needPage) : left(kind) > 0 && pageLeft(kind) > 0);

    const run = async (key, fn) => {
        setBusy(key);
        setError('');
        try {
            await fn();
        } catch (e) {
            setError(e.message);
        } finally {
            setBusy('');
            refreshMe();
        }
    };

    // AI answers land here, both right away and when a call left running in a closed tab is picked up on reopening
    const applyAssist = (mode, input, r) => {
        if (mode === 'ideas') setIdeas(Array.isArray(r.ideas) ? r.ideas : []);
        else if (mode === 'prompt') {
            set({ imagePrompt: r.image_prompt || '', videoPrompt: r.video_prompt || '' });
            setNotesVi(r.notes_vi || '');
        } else if (mode === 'improve') {
            if (r.prompt) set(input.kind === 'image' ? { imagePrompt: r.prompt } : { videoPrompt: r.prompt });
            setChanges(Array.isArray(r.changes_vi) ? r.changes_vi : []);
        } else if (mode === 'caption') setCaptions(r);
    };
    const applyRef = useRef(applyAssist);
    applyRef.current = applyAssist;
    const resumeCancelRef = useRef(null);
    useEffect(() => {
        const job = takePendingAssist();
        if (!job) return undefined;
        let stopped = false;
        const stop = () => {
            if (stopped) return;
            stopped = true;
            clearPendingAssist();
            setBusy('');
        };
        resumeCancelRef.current = stop;
        setBusy('resume');
        resumeAssist(api, job, { cancelled: () => stopped })
            .then(r => { if (!stopped) applyRef.current(job.mode, job.input, r); })
            .catch(e => { if (!stopped) setError(e.message); })
            .finally(stop);
        // a new api (session refreshed) re-runs this effect, which picks the same job up again
        return () => {
            stopped = true;
            setBusy(b => (b === 'resume' ? '' : b));
        };
    }, [api]);
    const assistRun = (key, mode, input) => run(key, async () => applyAssist(mode, input, await callAssist(api, { mode, input, masterId: draft.masterId || undefined })));

    const suggestIdeas = () => assistRun('ideas', 'ideas', { niche: draft.niche, notes: draft.notes, count: 3, avoid: ideas.map(x => x.title).filter(Boolean) });
    const writePrompts = () => assistRun('prompt', 'prompt', { idea: draft.idea, seconds: videoSeconds });
    const improve = kind => assistRun(`improve-${kind}`, 'improve', { kind, prompt: kind === 'image' ? draft.imagePrompt : draft.videoPrompt, feedback: draft.feedback });
    const writeCaptions = () => assistRun('caption', 'caption', { title: draft.title, concept: draft.idea || draft.videoPrompt || draft.imagePrompt });
    const aiBusy = ['ideas', 'prompt', 'caption', 'resume'].includes(busy) || busy.startsWith('improve');
    const updateGen = x => setGens(list => list.map(y => (y.id === x.id ? { ...y, ...x } : y)));
    const fixFrom = g => {
        setDraft(d => ({ ...d, ...fixPatch(g, d) }));
        setTimeout(() => stepRefs.current[1]?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    };

    const generate = kind => {
        const prompt = (kind === 'image' ? draft.imagePrompt : draft.videoPrompt).trim();
        const same = session.find(g => g.kind === kind && g.state === 'done' && g.prompt.trim() === prompt && (kind === 'image' || (g.image || '') === (draft.startImage || '')));
        if (same && !window.confirm(kind === 'image'
            ? 'Prompt này vừa tạo ra ảnh rồi. Tạo lại sẽ ra ảnh mới thay ảnh cũ (tốn thêm 1 lượt). Tiếp tục?'
            : 'Prompt và khung đầu này vừa tạo ra video rồi. Tạo thêm 1 bản nữa (tốn thêm 1 lượt)?')) return;
        startGenerate(kind);
    };
    const startGenerate = kind => run(`gen-${kind}`, async () => {
        const body = kind === 'image'
            ? { kind, prompt: draft.imagePrompt, ratio: draft.imageRatio, title: draft.title, refs: [...(draft.refUrl ? [draft.refUrl] : []), ...(upload ? [upload] : [])] }
            : { kind, prompt: draft.videoPrompt, model, seconds: videoSeconds, ratio: draft.videoRatio, image: draft.startImage || undefined, title: draft.title };
        const g = await api('generate', { ...body, pageId: draft.pageId || undefined, masterId: draft.masterId && !draft.masterId.startsWith('mine:') ? draft.masterId : undefined });
        if (!draft.since) set({ since: g.createdAt });
        setGens(list => [g, ...list.filter(x => x.id !== g.id)]);
        if (g.duplicate) setError(`${kind === 'image' ? 'Ảnh' : 'Video'} với đúng yêu cầu này đang được tạo — không tạo thêm bản trùng, không trừ lượt.`);
    });
    const videoFrom = g => {
        set({ startImage: g.urls[0] });
        setTimeout(() => videoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
    };
    // the newest image of this session becomes the video's first frame unless staff picked another one themselves
    const latestImageUrl = latestImage?.state === 'done' ? latestImage.urls[0] : '';
    useEffect(() => {
        if (!latestImageUrl) return;
        setDraft(d => (!d.startImage || sessionImages.some(g => g.urls?.[0] === d.startImage) ? { ...d, startImage: latestImageUrl } : d));
    }, [latestImageUrl]);

    const pickUpload = async e => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        try {
            setUpload(await fileToRef(file));
        } catch (err) {
            setError(err.message);
        }
    };

    const left = kind => Math.max(0, (me.limits[kind] || 0) - (me.usage[kind] || 0));
    // Video HD and 30 s only for pages taking off (admin setting); other pages get the fast model, up to 15 s
    const premium = !me.options.hdHotOnly || me.role === 'admin' || Boolean(page?.plan.premium);
    // HD only renders 30 s now (its shorter lengths cost more than its 30 s), so it shows only where 30 s is allowed
    const videoModels = me.options.videoModels.filter(m => m !== 'seedance_2.5' || (premium && me.options.allow30 && !me.options.no25));
    const model = videoModels.includes(draft.model) ? draft.model : videoModels[0];
    const seconds = model === 'seedance_2.5' ? [30] : me.options.videoSeconds.filter(s => s !== 30);
    const videoSeconds = seconds.includes(draft.seconds) ? draft.seconds : seconds.includes(15) ? 15 : seconds[0];

    // Steps open one after another: an idea opens the prompts, a prompt opens generation, a result opens captions
    const done = [
        draft.idea.trim().length >= 10,
        draft.imagePrompt.trim().length >= 10 || draft.videoPrompt.trim().length >= 10,
        latest.length > 0
    ];
    const open = [true, done[0], done[0] && done[1], done[0] && done[1] && done[2]];
    const openCount = open.filter(Boolean).length;
    const stepRefs = useRef([]);
    const seenOpen = useRef(openCount);
    useEffect(() => {
        if (openCount > seenOpen.current) {
            const el = stepRefs.current[openCount - 1];
            if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
        }
        seenOpen.current = openCount;
    }, [openCount]);
    const STEPS = ['Ý tưởng', 'Prompt', 'Tạo ảnh/video', 'Caption'];
    const results = (
        <div className={card}>
            <h3 className="mb-2 font-bold text-white">Kết quả phiên này</h3>
            <Notice tone="warn">Link ảnh/video chỉ lưu tạm — <b>tải về máy ngay</b> khi xong.</Notice>
            <div className="mt-3 space-y-3">
                {latest.length === 0 && <p className="text-sm text-slate-500">Chưa tạo gì.</p>}
                {sessionImages.length > 1 && <p className="text-xs text-slate-500">Ảnh mới đã thay {sessionImages.length - 1} ảnh trước — ảnh cũ vẫn ở tab Thư viện.</p>}
                {latest.map(g => (
                    <div key={g.id} className="space-y-1">
                        <Media g={g} className="max-h-[420px]" />
                        <div className="flex items-center justify-between gap-2">
                            <GenStatus g={g} />
                            {g.state === 'done' && <a className="inline-flex items-center gap-1 text-xs font-semibold text-amber-300 hover:underline" href={g.urls[0]} target="_blank" rel="noreferrer" download><Download size={12} /> Tải về</a>}
                        </div>
                        {g.kind === 'image' && g.state === 'done' && (draft.startImage === g.urls[0]
                            ? <p className="text-xs text-emerald-300">✓ Đang là khung đầu cho video</p>
                            : <button type="button" className={`${btnGhost} w-full`} onClick={() => videoFrom(g)}><Film size={14} /> Dùng ảnh này làm video</button>)}
                        <QaBox g={g} api={api} onSaved={updateGen} onFix={fixFrom} />
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
            <div className="min-w-0 space-y-4">
                <div className="xl:hidden"><JobStrip gens={gens} onOpen={onLibrary} /></div>
                <nav className="sticky top-0 z-10 -mx-1 flex gap-1 overflow-x-auto rounded-xl bg-slate-950/90 p-1 backdrop-blur">
                    {STEPS.map((name, i) => (
                        <button key={name} type="button" disabled={!open[i]} onClick={() => stepRefs.current[i]?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                            className={`flex flex-1 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-2 py-1.5 text-xs font-semibold sm:text-sm ${done[i] ? 'bg-emerald-500/15 text-emerald-300' : open[i] ? 'bg-amber-400/15 text-amber-200' : 'text-slate-600'}`}>
                            {done[i] ? <Check size={13} /> : <span>{i + 1}.</span>} {name}
                        </button>
                    ))}
                </nav>
                {error && <Notice tone="error" onClose={() => setError('')}>{error}</Notice>}
                {aiBusy && (
                    <p className="sticky top-11 z-10 flex items-center gap-2 rounded-lg border border-sky-400/40 bg-sky-950/95 px-3 py-2.5 text-sm font-medium text-sky-100 shadow-lg backdrop-blur">
                        <Loader2 size={16} className="shrink-0 animate-spin" />
                        {busy === 'resume' ? 'Đang lấy lại kết quả AI của lần trước…' : 'AI đang viết (10–40 giây).'} Bạn cứ chuyển app/tab — quay lại kết quả vẫn còn, không tốn thêm lượt.
                        {busy === 'resume' && <button type="button" className="ml-auto shrink-0 rounded px-2 py-0.5 font-semibold text-sky-100 hover:bg-sky-500/20" onClick={() => resumeCancelRef.current?.()}>Hủy</button>}
                    </p>
                )}

                <Step n={1} title="Chủ đề & ý tưởng" isOpen={open[0]} isDone={done[0]} refs={stepRefs}>
                    {needPage && !myPages.length && <Notice tone="warn">Bạn chưa được giao page nào nên chưa tạo được ảnh/video — nhờ admin giao page.</Notice>}
                    {myPages.length > 0 && <TodayPlan pages={myPages} masters={me.masters} current={draft.pageId} onTake={takeIdea} />}
                    {myPages.length > 0 && (
                        <div className="mb-3">
                            <span className={label}>Làm bài cho page</span>
                            <select className={input} value={draft.pageId} onChange={e => pickPage(e.target.value)}>
                                <option value="">— Chọn page —</option>
                                {myPages.map(p => <option key={p.id} value={p.id}>{p.name}{p.niche ? ` · ${p.niche}` : ''}</option>)}
                            </select>
                            {pageMasters.length > 0 && (
                                <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
                                    <span className="text-slate-400">Prompt nền hợp page:</span>
                                    {pageMasters.map((m, i) => (
                                        <button key={m.id} type="button" title={m.use} onClick={() => set({ masterId: m.id })}
                                            className={`rounded-full px-2 py-0.5 ${draft.masterId === m.id ? 'bg-amber-400 text-slate-950' : 'bg-slate-800 text-slate-200 hover:bg-slate-700'}`}>
                                            {i === 0 ? '★ ' : ''}{m.name}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    <div className="grid gap-3 md:grid-cols-2">
                        <div>
                            <span className={label}>Prompt nền cho AI</span>
                            <select className={input} value={draft.masterId} disabled={mustPickPage} onChange={e => set({ masterId: e.target.value })}>
                                <option value="">{mustPickPage ? '— Chọn page ở trên trước —' : '— Không dùng —'}</option>
                                {lockedTo ? (
                                    <optgroup label={`Prompt admin chọn cho ${page.name}`}>
                                        {pageMasters.map((m, i) => <option key={m.id} value={m.id}>{i === 0 ? '★ ' : ''}{m.name}</option>)}
                                    </optgroup>
                                ) : !mustPickPage && fitting.length > 0 && (
                                    <optgroup label={pageMasters.length ? `⭐ Gợi ý cho ${page.name}` : '⭐ Hợp ngách page của bạn'}>
                                        {fitting.map(m => <option key={`fit-${m.id}`} value={m.id}>⭐ {m.name}</option>)}
                                    </optgroup>
                                )}
                                {!lockedTo && !mustPickPage && masterGroups.map(([group, list]) => (
                                    <optgroup key={group} label={group}>
                                        {list.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                                    </optgroup>
                                ))}
                                {me.myPrompts?.length > 0 && (
                                    <optgroup label="Prompt tôi sưu tầm">
                                        {me.myPrompts.map(m => <option key={m.id} value={`mine:${m.id}`}>{m.name}{m.niche ? ` · ${m.niche}` : ''}</option>)}
                                    </optgroup>
                                )}
                            </select>
                            <p className="mt-1 text-xs text-slate-400">
                                {chosenMaster ? <><b className="text-slate-300">{chosenMaster.group}:</b> {chosenMaster.use || 'Chưa có mô tả.'}</> : mustPickPage ? 'Chọn page trước — prompt nền của page tự điền.' : page && !lockedTo && me.role !== 'admin' ? 'Admin chưa chọn prompt cho page này — tạm chọn theo nhóm chủ đề, hoặc báo admin.' : myPages.length ? 'Chọn page ở trên là prompt nền tự điền; hoặc chọn theo nhóm chủ đề.' : 'Chọn prompt theo nhóm chủ đề; mục ⭐ là hợp với ngách page của bạn.'}
                            </p>
                        </div>
                        <div>
                            <span className={label}>Ngách page của bạn</span>
                            <input className={input} value={draft.niche} onChange={e => set({ niche: e.target.value })} placeholder="VD: chim làm tổ, sửa nhà mini, động vật lạ…" />
                        </div>
                    </div>
                    <div className="mt-3">
                        <span className={label}>Ghi chú cho AI (tuỳ chọn)</span>
                        <input className={input} value={draft.notes} onChange={e => set({ notes: e.target.value })} placeholder="VD: muốn chủ đề mùa thu, tránh lặp ý tưởng con cú…" />
                    </div>
                    <button type="button" className={`${btnGhost} mt-3`} disabled={!!busy || left('ai') < 1} onClick={suggestIdeas}>
                        {busy === 'ideas' ? <Loader2 size={15} className="animate-spin" /> : <Bot size={15} />} {ideas.length ? 'Gợi ý 3 ý tưởng khác' : 'Gợi ý 3 ý tưởng'}
                    </button>
                    {ideas.length > 0 && (
                        <div className="mt-3 grid gap-2 md:grid-cols-2">
                            {ideas.map((idea, i) => (
                                <button key={i} type="button" onClick={() => set({ idea: `${idea.title}. ${idea.concept}`, title: idea.title })}
                                    className="rounded-lg border border-slate-700 bg-slate-950 p-3 text-left text-sm hover:border-amber-400">
                                    <strong className="block text-amber-200">{idea.title}</strong>
                                    <span className="mt-1 block text-slate-300">{idea.concept}</span>
                                    {idea.why_vi && <span className="mt-1 block text-xs text-slate-500">💡 {idea.why_vi}</span>}
                                </button>
                            ))}
                        </div>
                    )}
                    <div className="mt-3">
                        <span className={label}>Ý tưởng đã chọn (sửa thoải mái)</span>
                        <textarea className={`${input} min-h-[80px]`} value={draft.idea} onChange={e => set({ idea: e.target.value })} placeholder="Tự viết ý tưởng hoặc bấm vào một gợi ý ở trên" />
                        {!done[0] && <p className="mt-1 text-xs text-slate-500">Chọn hoặc viết ý tưởng (ít nhất vài chữ) để sang bước 2.</p>}
                    </div>
                </Step>

                <Step n={2} title="Prompt" isOpen={open[1]} isDone={done[1]} refs={stepRefs}>
                    <div className="flex flex-wrap items-end gap-3">
                        <div>
                            <span className={label}>Độ dài video</span>
                            <select className={input} value={videoSeconds} onChange={e => set({ seconds: Number(e.target.value) })}>
                                {seconds.map(s => <option key={s} value={s}>{s} giây</option>)}
                            </select>
                        </div>
                        <button type="button" className={btnGhost} disabled={!!busy || draft.idea.trim().length < 10 || left('ai') < 1} onClick={writePrompts}>
                            {busy === 'prompt' ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />} AI viết prompt ảnh + video
                        </button>
                    </div>
                    {notesVi && <p className="mt-2 text-xs text-sky-200">ℹ️ {notesVi}</p>}
                    <div className="mt-3 grid gap-3 lg:grid-cols-2">
                        {[['image', 'Prompt ảnh (khung đầu)', 'imagePrompt'], ['video', 'Prompt video', 'videoPrompt']].map(([kind, title, field]) => (
                            <div key={kind}>
                                <div className="mb-1 flex items-center justify-between">
                                    <span className={label}>{title}</span>
                                    <span className="text-xs text-slate-500">{fmt(draft[field].length)} ký tự</span>
                                </div>
                                <textarea className={`${input} min-h-[220px] font-mono text-xs`} value={draft[field]} onChange={e => set({ [field]: e.target.value })} />
                                <button type="button" className={`${btnGhost} mt-2`} disabled={!!busy || draft[field].trim().length < 10 || left('ai') < 1} onClick={() => improve(kind)}>
                                    {busy === `improve-${kind}` ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} AI tối ưu
                                </button>
                            </div>
                        ))}
                    </div>
                    <div className="mt-3">
                        <span className={label}>Kết quả trước chưa ưng? Mô tả lỗi để AI sửa</span>
                        <input className={input} value={draft.feedback} onChange={e => set({ feedback: e.target.value })} placeholder="VD: con chim bị biến dạng ở giây 8, kết thúc nhạt…" />
                    </div>
                    {changes.length > 0 && <ul className="mt-2 list-disc pl-5 text-xs text-slate-300">{changes.map((c, i) => <li key={i}>{c}</li>)}</ul>}
                </Step>

                <Step n={3} title="Tạo ảnh & video" isOpen={open[2]} isDone={done[2]} refs={stepRefs}>
                    {page
                        ? <p className="mb-3 text-xs text-slate-400">Đang làm cho <b className="text-slate-200">{page.name}</b> · hôm nay còn <b className="text-amber-300">{pageLeft('videos')}</b> lượt video, {pageLeft('images')} lượt ảnh cho page này (kế hoạch {page.plan.target} bài).</p>
                        : needPage && <Notice tone="warn">Chọn page ở bước 1 trước — mỗi ảnh/video được tính vào kế hoạch của 1 page.</Notice>}
                    <div className="mb-3">
                        <span className={label}>Tiêu đề (để bạn nhớ)</span>
                        <input className={input} value={draft.title} onChange={e => set({ title: e.target.value })} />
                    </div>
                    <div className="grid gap-4 lg:grid-cols-2">
                        <div className="space-y-2 rounded-lg border border-slate-800 p-3">
                            <strong className="flex items-center gap-2 text-sm text-white"><ImageIcon size={15} /> Ảnh · còn {left('images')}/{me.limits.images} hôm nay</strong>
                            <select className={input} value={draft.imageRatio} onChange={e => set({ imageRatio: e.target.value })}>
                                {me.options.imageRatios.map(r => <option key={r} value={r}>Tỉ lệ {r}</option>)}
                            </select>
                            <select className={input} value={draft.refUrl} onChange={e => set({ refUrl: e.target.value })}>
                                <option value="">Ảnh tham chiếu từ thư viện: không</option>
                                {doneImages.map(g => <option key={g.id} value={g.urls[0]}>{g.title || g.prompt.slice(0, 50)}</option>)}
                            </select>
                            <div className="flex items-center gap-2">
                                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickUpload} />
                                <button type="button" className={btnGhost} onClick={() => fileRef.current?.click()}><Upload size={14} /> Ảnh từ máy</button>
                                {upload && <span className="flex items-center gap-1 text-xs text-slate-300">{upload.file_name}<button type="button" onClick={() => setUpload(null)}><X size={12} /></button></span>}
                            </div>
                            <button type="button" className={`${btnMain} w-full`} disabled={!!busy || !me.ready.seedvis || me.options.maintenance?.image || !canMake('images') || draft.imagePrompt.trim().length < 10} onClick={() => generate('image')}>
                                {busy === 'gen-image' ? <Loader2 size={15} className="animate-spin" /> : <ImageIcon size={15} />} {me.options.maintenance?.image ? 'Đang bảo trì' : latestImage ? 'Tạo lại ảnh (thay ảnh cũ)' : 'Tạo ảnh'}
                            </button>
                        </div>
                        <div ref={videoRef} className="space-y-2 rounded-lg border border-slate-800 p-3">
                            <strong className="flex items-center gap-2 text-sm text-white"><Film size={15} /> Video · {me.options.gencpuFree ? `nhanh ≤15s không giới hạn (GenCPU)${me.options.share25 ? ` · HD 30s còn ${Math.max(0, me.options.share25.cap - me.options.share25.used)}/${me.options.share25.cap} hôm nay` : ''}` : `còn ${left('videos')}/${me.limits.videos} hôm nay`}</strong>
                            <div className="grid grid-cols-2 gap-2">
                                <select className={input} value={model} onChange={e => set({ model: e.target.value, seconds: e.target.value !== 'seedance_2.5' && draft.seconds === 30 ? 15 : draft.seconds })}>
                                    {videoModels.map(m => <option key={m} value={m}>{modelLabel(m)}</option>)}
                                </select>
                                <select className={input} value={draft.videoRatio} onChange={e => set({ videoRatio: e.target.value })}>
                                    {me.options.videoRatios.map(r => <option key={r} value={r}>Tỉ lệ {r}</option>)}
                                </select>
                            </div>
                            <select className={input} value={draft.startImage} onChange={e => set({ startImage: e.target.value })}>
                                <option value="">Khung đầu: không (chỉ dùng prompt)</option>
                                {doneImages.map(g => <option key={g.id} value={g.urls[0]}>{g === latestImage ? '★ Ảnh mới nhất · ' : ''}{g.title || g.prompt.slice(0, 50)}</option>)}
                            </select>
                            {draft.startImage && <img src={draft.startImage} alt="Khung đầu" className="h-24 rounded-md border border-slate-700 object-cover" />}
                            {!premium && page && <p className="text-xs text-amber-300/80">Video HD và 30s dành cho page "Đang nổ". Page này đang ở mức "{page.plan.label}" nên dùng video nhanh, tối đa 15s.</p>}
                            <p className="text-xs text-slate-500">Mẹo: tạo ảnh khung đầu trước, ưng rồi mới chọn làm khung đầu cho video — đỡ phí lượt video.</p>
                            <button type="button" className={`${btnMain} w-full`} disabled={!!busy || !me.ready.seedvis || me.options.maintenance?.video || !canMake('videos') || draft.videoPrompt.trim().length < 10} onClick={() => generate('video')}>
                                {busy === 'gen-video' ? <Loader2 size={15} className="animate-spin" /> : <Film size={15} />} {me.options.maintenance?.video ? 'Đang bảo trì' : `Tạo video ${videoSeconds}s`}
                            </button>
                        </div>
                    </div>
                    <div className="mt-4 xl:hidden">{results}</div>
                </Step>

                <Step n={4} title="Caption" isOpen={open[3]} isDone={done[3]} refs={stepRefs}>
                    <button type="button" className={btnGhost} disabled={!!busy || (!draft.idea && !draft.videoPrompt && !draft.imagePrompt) || left('ai') < 1} onClick={writeCaptions}>
                        {busy === 'caption' ? <Loader2 size={15} className="animate-spin" /> : <Bot size={15} />} AI viết caption
                    </button>
                    {captions && (
                        <div className="mt-3 space-y-2">
                            {(captions.captions || []).map((c, i) => (
                                <div key={i} className="flex items-start gap-2 rounded-lg bg-slate-950 p-2 text-sm text-slate-100">
                                    <span className="flex-1">{c}</span><CopyButton small text={`${c}\n\n${(captions.hashtags || []).join(' ')}`} />
                                </div>
                            ))}
                            {captions.hashtags?.length > 0 && <p className="text-xs text-slate-400">{captions.hashtags.join(' ')}</p>}
                        </div>
                    )}
                    <p className="mt-3 text-xs text-slate-500">Xong: tải video về, đăng lên page. Page trong BM tự đồng bộ bài — vào tab Page của tôi bấm "Gắn prompt" cho bài để cả team học.</p>
                </Step>
            </div>

            <aside className="hidden space-y-3 xl:sticky xl:top-4 xl:block xl:self-start">{results}</aside>
        </div>
    );
}

// ---------------- Thư viện ----------------

function LibraryTab({ api, gens, setGens, onUse, onFix, onVideo }) {
    const [filter, setFilter] = useState('all');
    const needQa = g => g.state === 'done' && !g.qa;
    const shown = gens.filter(g => filter === 'all' || (filter === 'qa' ? needQa(g) : g.kind === filter));
    const qaCount = gens.filter(needQa).length;
    const updateGen = x => setGens(list => list.map(y => (y.id === x.id ? { ...y, ...x } : y)));
    const hide = async g => {
        if (!window.confirm(g.state === 'queued' ? 'Huỷ job đang chờ (hoàn lượt) và ẩn?' : 'Ẩn mục này khỏi thư viện?')) return;
        await api('generation-hide', { id: g.id }).catch(e => window.alert(e.message));
        setGens(list => list.filter(x => x.id !== g.id));
    };
    return (
        <div className="space-y-4">
            <Notice tone="warn">Ảnh/video nằm trên máy chủ tạo ảnh/video và <b>có thể bị xoá sau vài ngày</b>. QA xong là tải về máy ngay — hệ thống chỉ giữ link. Trên điện thoại: bấm Tải về → video mở ra → bấm ⋮ (hoặc giữ tay lên video) → Tải xuống / Lưu video.</Notice>
            <div className="flex flex-wrap gap-2">
                {[['all', 'Tất cả'], ['qa', `Cần QA${qaCount ? ` (${qaCount})` : ''}`], ['image', 'Ảnh'], ['video', 'Video']].map(([id, text]) => (
                    <button key={id} type="button" className={filter === id ? btnMain : btnGhost} onClick={() => setFilter(id)}>{text}</button>
                ))}
            </div>
            {shown.length === 0 && <p className="text-sm text-slate-500">Chưa có nội dung.</p>}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                {shown.map(g => (
                    <div key={g.id} className={`${card} flex flex-col gap-2`}>
                        <Media g={g} className="aspect-[9/16] max-h-[480px]" />
                        <div className="flex items-center justify-between">
                            <span className="text-xs text-slate-400">{g.kind === 'video' ? `🎬 ${g.seconds}s · ${modelLabel(g.model)}` : `🖼️ ${g.ratio}`} · {new Date(g.createdAt).toLocaleString('vi-VN')}</span>
                            <GenStatus g={g} />
                        </div>
                        {g.title && <strong className="text-sm text-white">{g.title}</strong>}
                        {g.pageName && <span className="text-xs text-slate-500">Page: {g.pageName}</span>}
                        <details className="text-xs text-slate-400"><summary className="cursor-pointer">Prompt</summary><p className="mt-1 whitespace-pre-wrap">{g.prompt}</p></details>
                        <QaBox g={g} api={api} onSaved={updateGen} onFix={onFix} />
                        <div className="mt-auto flex flex-wrap gap-2">
                            {g.state === 'done' && <a className={btnMain} href={g.urls[0]} target="_blank" rel="noreferrer" download><Download size={14} /> Tải về</a>}
                            <CopyButton text={g.prompt} />
                            {g.kind === 'image' && g.state === 'done' && <button type="button" className={btnGhost} onClick={() => onVideo(g)}><Film size={14} /> Làm video từ ảnh này</button>}
                            <button type="button" className={btnGhost} onClick={() => onUse(g)}><RefreshCw size={14} /> Dùng lại</button>
                            <button type="button" className={btnGhost} onClick={() => hide(g)} title="Ẩn"><Trash2 size={14} /></button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ---------------- Page của tôi ----------------

const EMPTY_PAGE = { id: '', name: '', url: '', niche: '', status: 'building', followers: 0, note: '' };
const emptyPost = pageId => ({ id: '', pageId, url: '', postedAt: today(), type: 'reel', title: '', generationId: '', views: 0, reactions: 0, comments: 0, shares: 0 });

function PagesTab({ api, gens, admin }) {
    const [data, setData] = useState({ pages: [], posts: [] });
    const [pageForm, setPageForm] = useState(null);
    const [postForm, setPostForm] = useState(null);
    const [error, setError] = useState('');
    const load = useCallback(() => api('pages').then(setData).catch(e => setError(e.message)), [api]);
    useEffect(() => { load(); }, [load]);

    const save = async (action, body, done) => {
        setError('');
        try {
            await api(action, body);
            done();
            load();
        } catch (e) {
            setError(e.message);
        }
    };
    const statsOf = pageId => {
        const posts = data.posts.filter(p => p.pageId === pageId);
        return { count: posts.length, views: posts.reduce((s, p) => s + p.views, 0), inter: posts.reduce((s, p) => s + p.reactions + p.comments + p.shares, 0), posts };
    };
    const num = (field, text) => (
        <label className="block"><span className={label}>{text}</span>
            <input type="number" min="0" className={input} value={postForm[field]} onChange={e => setPostForm(f => ({ ...f, [field]: e.target.value }))} /></label>
    );

    return (
        <div className="space-y-4">
            {error && <Notice tone="error" onClose={() => setError('')}>{error}</Notice>}
            <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-slate-400">Page do admin giao cho bạn. Page trong BM được <b className="text-sky-300">đồng bộ số liệu tự động</b> (vài lần/ngày) — bạn chỉ cần gắn prompt cho bài để cả team học. Page ngoài BM thì ghi bài và số liệu tay (cập nhật sau 1 và 7 ngày).</p>
                {admin && <button type="button" className={btnMain} onClick={() => setPageForm({ ...EMPTY_PAGE })}><Plus size={15} /> Thêm page</button>}
            </div>
            {!admin && !data.pages.length && <Notice>Bạn chưa được giao page nào — nhờ admin thêm page cho bạn trong tab Quản trị.</Notice>}

            {pageForm && (
                <div className={`${card} grid gap-3 md:grid-cols-2`}>
                    <label><span className={label}>Tên page</span><input className={`${input} disabled:opacity-50`} disabled={!admin} value={pageForm.name} onChange={e => setPageForm(f => ({ ...f, name: e.target.value }))} /></label>
                    <label><span className={label}>Link page</span><input className={`${input} disabled:opacity-50`} disabled={!admin} value={pageForm.url} onChange={e => setPageForm(f => ({ ...f, url: e.target.value }))} placeholder="https://facebook.com/..." /></label>
                    <label><span className={label}>Ngách / chủ đề</span><input className={input} value={pageForm.niche} onChange={e => setPageForm(f => ({ ...f, niche: e.target.value }))} /></label>
                    <label><span className={label}>Trạng thái</span>
                        <select className={input} value={pageForm.status} onChange={e => setPageForm(f => ({ ...f, status: e.target.value }))}>
                            {Object.entries(PAGE_STATUS).map(([id, text]) => <option key={id} value={id}>{text}</option>)}
                        </select></label>
                    <label><span className={label}>Số người theo dõi hiện tại</span><input type="number" min="0" disabled={Boolean(pageForm.fbPageId)} className={`${input} disabled:opacity-50`} value={pageForm.followers} onChange={e => setPageForm(f => ({ ...f, followers: e.target.value }))} /></label>
                    <p className="self-end text-xs text-slate-500">{pageForm.fbPageId ? 'Page trong BM: số follow tự cập nhật.' : 'Cập nhật số follow vài ngày/lần để tab Insight tính được mức tăng.'}</p>
                    <label className="md:col-span-2"><span className={label}>Ghi chú</span><textarea className={input} value={pageForm.note} onChange={e => setPageForm(f => ({ ...f, note: e.target.value }))} /></label>
                    <div className="flex gap-2 md:col-span-2">
                        <button type="button" className={btnMain} onClick={() => save('page-save', pageForm, () => setPageForm(null))}><Save size={15} /> Lưu</button>
                        <button type="button" className={btnGhost} onClick={() => setPageForm(null)}>Huỷ</button>
                    </div>
                </div>
            )}

            {postForm && postForm.fbId && (
                <div className={`${card} grid gap-3 md:grid-cols-2`}>
                    <p className="text-sm text-slate-300 md:col-span-2">Bài đồng bộ từ Facebook — số liệu tự cập nhật. Đặt tên dễ nhớ và gắn nội dung đã tạo để cả team học prompt.</p>
                    <label><span className={label}>Tên bài</span><input className={input} value={postForm.title} onChange={e => setPostForm(f => ({ ...f, title: e.target.value }))} /></label>
                    <label><span className={label}>Nội dung từ thư viện</span>
                        <select className={input} value={postForm.generationId} onChange={e => setPostForm(f => ({ ...f, generationId: e.target.value }))}>
                            <option value="">— Không gắn —</option>
                            {gens.filter(g => g.state === 'done').map(g => <option key={g.id} value={g.id}>{g.kind === 'video' ? '🎬' : '🖼️'} {g.title || g.prompt.slice(0, 60)}</option>)}
                        </select></label>
                    <div className="flex gap-2 md:col-span-2">
                        <button type="button" className={btnMain} onClick={() => save('post-save', postForm, () => setPostForm(null))}><Save size={15} /> Lưu</button>
                        <button type="button" className={btnGhost} onClick={() => setPostForm(null)}>Huỷ</button>
                    </div>
                </div>
            )}

            {postForm && !postForm.fbId && (
                <div className={`${card} grid gap-3 md:grid-cols-4`}>
                    <label className="md:col-span-2"><span className={label}>Page</span>
                        <select className={input} value={postForm.pageId} onChange={e => setPostForm(f => ({ ...f, pageId: e.target.value }))}>
                            {data.pages.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select></label>
                    <label><span className={label}>Ngày đăng</span><input type="date" className={input} value={postForm.postedAt} onChange={e => setPostForm(f => ({ ...f, postedAt: e.target.value }))} /></label>
                    <label><span className={label}>Loại</span>
                        <select className={input} value={postForm.type} onChange={e => setPostForm(f => ({ ...f, type: e.target.value }))}><option value="reel">Reel / video</option><option value="photo">Ảnh</option></select></label>
                    <label className="md:col-span-2"><span className={label}>Tiêu đề / caption ngắn</span><input className={input} value={postForm.title} onChange={e => setPostForm(f => ({ ...f, title: e.target.value }))} /></label>
                    <label className="md:col-span-2"><span className={label}>Link bài</span><input className={input} value={postForm.url} onChange={e => setPostForm(f => ({ ...f, url: e.target.value }))} placeholder="https://facebook.com/..." /></label>
                    <label className="md:col-span-4"><span className={label}>Nội dung từ thư viện (để cả team học prompt)</span>
                        <select className={input} value={postForm.generationId} onChange={e => setPostForm(f => ({ ...f, generationId: e.target.value }))}>
                            <option value="">— Không gắn —</option>
                            {gens.filter(g => g.state === 'done').map(g => <option key={g.id} value={g.id}>{g.kind === 'video' ? '🎬' : '🖼️'} {g.title || g.prompt.slice(0, 60)}</option>)}
                        </select></label>
                    {num('views', 'Lượt xem')}{num('reactions', 'Cảm xúc')}{num('comments', 'Bình luận')}{num('shares', 'Chia sẻ')}
                    <div className="flex gap-2 md:col-span-4">
                        <button type="button" className={btnMain} onClick={() => save('post-save', postForm, () => setPostForm(null))}><Save size={15} /> Lưu bài</button>
                        <button type="button" className={btnGhost} onClick={() => setPostForm(null)}>Huỷ</button>
                    </div>
                </div>
            )}

            {data.pages.length === 0 && !pageForm && <p className="text-sm text-slate-500">Chưa có page nào. Thêm page đầu tiên để bắt đầu theo dõi.</p>}
            {data.pages.map(page => {
                const s = statsOf(page.id);
                return (
                    <div key={page.id} className={card}>
                        <div className="flex flex-wrap items-start justify-between gap-2">
                            <div>
                                <h3 className="font-bold text-white">{page.url ? <a href={page.url} target="_blank" rel="noreferrer" className="hover:underline">{page.name}</a> : page.name}
                                    <span className="ml-2 rounded-full bg-slate-800 px-2 py-0.5 text-xs font-medium text-slate-300">{PAGE_STATUS[page.status]}</span>
                                    {page.fbPageId && <span className="ml-1 rounded-full bg-sky-500/15 px-2 py-0.5 text-xs font-medium text-sky-300" title={page.syncError || 'Số liệu đồng bộ tự động từ BM'}>BM · {page.syncError ? '⚠️ lỗi đồng bộ' : page.syncedAt ? `cập nhật ${clock(page.syncedAt)}` : 'chờ đồng bộ'}</span>}</h3>
                                <p className="text-xs text-slate-400">{page.niche}{page.followers ? ` · ${fmt(page.followers)} follow` : ''}{page.note ? ` · ${page.note}` : ''}</p>
                                <p className="mt-1 text-xs text-slate-300">{s.count} bài · {fmt(s.views)} lượt xem · {fmt(s.inter)} tương tác{s.count ? ` · TB ${fmt(Math.round(s.views / s.count))} view/bài` : ''}</p>
                            </div>
                            <div className="flex gap-2">
                                {!page.fbPageId && <button type="button" className={btnMain} onClick={() => setPostForm(emptyPost(page.id))}><Plus size={14} /> Bài đăng</button>}
                                <button type="button" className={btnGhost} onClick={() => setPageForm({ ...EMPTY_PAGE, ...page })}>Sửa</button>
                                {admin && !page.fbPageId && <button type="button" className={btnGhost} onClick={() => window.confirm(`Xoá page "${page.name}" và toàn bộ bài đã ghi?`) && save('page-delete', { id: page.id }, () => {})}><Trash2 size={14} /></button>}
                            </div>
                        </div>
                        {s.posts.length > 0 && (
                            <div className="mt-3 overflow-x-auto">
                                <table className="w-full text-left text-xs text-slate-300">
                                    <thead className="text-slate-500"><tr><th className="py-1">Ngày</th><th>Bài</th><th className="text-right">Xem</th><th className="text-right">Cảm xúc</th><th className="text-right">BL</th><th className="text-right">Share</th><th /></tr></thead>
                                    <tbody>
                                        {s.posts.map(p => (
                                            <tr key={p.id} className="border-t border-slate-800">
                                                <td className="py-1.5">{p.postedAt}</td>
                                                <td>{p.type === 'photo' ? '🖼️' : '🎬'} {p.url ? <a href={p.url} target="_blank" rel="noreferrer" className="hover:underline">{p.title || 'Xem bài'}</a> : p.title || '—'}</td>
                                                <td className="text-right">{fmt(p.views)}</td><td className="text-right">{fmt(p.reactions)}</td><td className="text-right">{fmt(p.comments)}</td><td className="text-right">{fmt(p.shares)}</td>
                                                <td className="text-right">
                                                    <button type="button" className="px-1 text-amber-300 hover:underline" onClick={() => setPostForm({ ...emptyPost(page.id), ...p })}>{p.fbId ? (p.generationId ? 'Đổi prompt' : 'Gắn prompt') : 'Cập nhật số'}</button>
                                                    {!p.fbId && <button type="button" className="px-1 text-slate-500 hover:text-red-300" onClick={() => window.confirm('Xoá bài này?') && save('post-delete', { id: p.id }, () => {})}><Trash2 size={12} /></button>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

// ---------------- Bảng vàng ----------------

function BoardTab({ api, onUsePrompt }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    useEffect(() => { api('leaderboard').then(setData).catch(e => setError(e.message)); }, [api]);
    if (error) return <Notice tone="error">{error}</Notice>;
    if (!data) return <Loader2 className="animate-spin text-slate-400" />;
    return (
        <div className="grid gap-4 xl:grid-cols-[320px_minmax(0,1fr)]">
            <div className={card}>
                <h3 className="mb-2 font-bold text-white">Nhân sự · 30 ngày</h3>
                {data.staff.length === 0 && <p className="text-sm text-slate-500">Chưa ai ghi bài.</p>}
                <ol className="space-y-1.5 text-sm">
                    {data.staff.map((s, i) => (
                        <li key={i} className="flex items-center justify-between gap-2 text-slate-200">
                            <span>{['🥇', '🥈', '🥉'][i] || `${i + 1}.`} {s.staffName}</span>
                            <span className="text-xs text-slate-400">{s.posts} bài · {fmt(s.views)} view</span>
                        </li>
                    ))}
                </ol>
            </div>
            <div className="space-y-3">
                <p className="text-sm text-slate-400">Top bài của cả team trong 30 ngày (xếp theo view + tương tác). Học cách ra đề, mở đầu và kết của bài thắng rồi áp dụng cho page của bạn.</p>
                {data.top.length === 0 && <p className="text-sm text-slate-500">Chưa có dữ liệu.</p>}
                {data.top.map((p, i) => (
                    <div key={i} className={card}>
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                            <strong className="text-white">#{i + 1} {p.url ? <a href={p.url} target="_blank" rel="noreferrer" className="hover:underline">{p.title || 'Bài đăng'}</a> : p.title || 'Bài đăng'}</strong>
                            <span className="text-xs text-slate-400">{p.staffName} · {p.pageName}{p.niche ? ` (${p.niche})` : ''} · {p.postedAt}</span>
                        </div>
                        <p className="mt-1 text-xs text-slate-300">👁 {fmt(p.views)} · ❤️ {fmt(p.reactions)} · 💬 {fmt(p.comments)} · 🔁 {fmt(p.shares)}</p>
                        {p.prompt && (
                            <details className="mt-2 text-xs text-slate-400">
                                <summary className="cursor-pointer">Xem prompt đã dùng</summary>
                                <p className="mt-1 whitespace-pre-wrap">{p.prompt}</p>
                                <div className="mt-2 flex gap-2"><CopyButton text={p.prompt} /><button type="button" className={btnGhost} onClick={() => onUsePrompt(p)}><RefreshCw size={14} /> Mở trong Tạo nội dung</button></div>
                            </details>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

// ---------------- Prompt của tôi ----------------

const EMPTY_MINE = { id: '', name: '', niche: '', source: '', text: '' };

function MyPromptsTab({ api, refreshMe, onUse }) {
    const [list, setList] = useState(null);
    const [form, setForm] = useState(null);
    const [error, setError] = useState('');
    const load = useCallback(() => api('prompts').then(r => setList(r.prompts)).catch(e => setError(e.message)), [api]);
    useEffect(() => { load(); }, [load]);
    const save = async body => {
        setError('');
        try {
            await api('prompt-save', body);
            setForm(null);
            load();
            refreshMe();
        } catch (e) {
            setError(e.message);
        }
    };
    const set = patch => setForm(f => ({ ...f, ...patch }));
    return (
        <div className="space-y-4">
            {error && <Notice tone="error" onClose={() => setError('')}>{error}</Notice>}
            <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="max-w-3xl text-sm text-slate-400">Prompt bạn sưu tầm được (tiếng gì cũng được). Chọn nó ở bước 1 của Tạo nội dung — AI học khung của prompt rồi viết/tối ưu prompt mới cho ý tưởng của bạn. Chỉ bạn thấy.</p>
                <button type="button" className={btnMain} onClick={() => setForm({ ...EMPTY_MINE })}><Plus size={15} /> Thêm prompt</button>
            </div>
            {form && (
                <div className={`${card} grid gap-3 md:grid-cols-3`}>
                    <label><span className={label}>Tên</span><input className={input} value={form.name} onChange={e => set({ name: e.target.value })} placeholder="VD: Tổ chim timelapse" /></label>
                    <label><span className={label}>Ngách</span><input className={input} value={form.niche} onChange={e => set({ niche: e.target.value })} /></label>
                    <label><span className={label}>Nguồn (tuỳ chọn)</span><input className={input} value={form.source} onChange={e => set({ source: e.target.value })} placeholder="Link / nhóm / người chia sẻ" /></label>
                    <label className="md:col-span-3"><span className={label}>Nội dung prompt</span><textarea className={`${input} min-h-[180px] font-mono text-xs`} value={form.text} onChange={e => set({ text: e.target.value })} /></label>
                    <div className="flex gap-2 md:col-span-3">
                        <button type="button" className={btnMain} disabled={!form.name.trim() || form.text.trim().length < 20} onClick={() => save(form)}><Save size={15} /> Lưu</button>
                        <button type="button" className={btnGhost} onClick={() => setForm(null)}>Huỷ</button>
                    </div>
                </div>
            )}
            {!list && !error && <Loader2 className="animate-spin text-slate-400" />}
            {list?.length === 0 && !form && <p className="text-sm text-slate-500">Chưa có prompt nào.</p>}
            <div className="grid gap-3 lg:grid-cols-2">
                {list?.map(p => (
                    <div key={p.id} className={card}>
                        <div className="flex flex-wrap items-start justify-between gap-2">
                            <div>
                                <strong className="text-white">{p.name}</strong>
                                <p className="text-xs text-slate-400">{[p.niche, p.source].filter(Boolean).join(' · ')}</p>
                            </div>
                            <div className="flex gap-2">
                                <button type="button" className={btnMain} onClick={() => onUse(p.id)}><Wand2 size={14} /> Dùng với AI</button>
                                <CopyButton text={p.text} />
                                <button type="button" className={btnGhost} onClick={() => setForm({ ...EMPTY_MINE, ...p })}>Sửa</button>
                                <button type="button" className={btnGhost} onClick={() => window.confirm(`Xoá "${p.name}"?`) && save({ id: p.id, delete: true })}><Trash2 size={14} /></button>
                            </div>
                        </div>
                        <p className="mt-2 line-clamp-4 whitespace-pre-wrap text-xs text-slate-400">{p.text}</p>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ---------------- Insight ----------------

function Sparkline({ values, color = '#fbbf24', title }) {
    if (!values || values.length < 2) return null;
    const min = Math.min(...values);
    const span = Math.max(1, Math.max(...values) - min);
    const points = values.map((v, i) => `${(i / (values.length - 1)) * 100},${28 - ((v - min) / span) * 26}`).join(' ');
    return <svg viewBox="0 0 100 30" className="h-8 w-28" preserveAspectRatio="none"><title>{title}</title><polyline points={points} fill="none" stroke={color} strokeWidth="2" vectorEffect="non-scaling-stroke" /></svg>;
}

function InsightTab({ api, admin }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [open, setOpen] = useState({});
    const [syncing, setSyncing] = useState(false);
    useEffect(() => {
        let alive = true;
        api('insights').then(d => alive && setData(d)).catch(e => alive && setError(e.message));
        // Pages in the BM refresh themselves when their numbers are over 2 hours old
        setSyncing(true);
        api('sync', {})
            .then(r => (r.synced && alive ? api('insights').then(setData) : null))
            .catch(() => {})
            .finally(() => alive && setSyncing(false));
        return () => { alive = false; };
    }, [api]);
    if (error) return <Notice tone="error">{error}</Notice>;
    if (!data) return <Loader2 className="animate-spin text-slate-400" />;
    const sign = n => (n > 0 ? `+${fmt(n)}` : fmt(n));
    return (
        <div className="space-y-4">
            <p className="text-sm text-slate-400">
                {admin ? 'Page của từng thành viên' : 'Page của bạn'} trong 30 ngày. Page trong BM lấy số liệu tự động (lượt xem, tiếp cận, follow mới, thời gian xem); page ngoài BM dùng số nhập ở tab Page của tôi.
                {syncing && <span className="ml-2 inline-flex items-center gap-1 text-sky-300"><Loader2 size={12} className="animate-spin" /> đang cập nhật từ Facebook…</span>}
            </p>
            {data.staff.length === 0 && <p className="text-sm text-slate-500">Chưa có dữ liệu.</p>}
            {admin && data.staff.length > 0 && <TeamOverview staff={data.staff} />}
            {data.staff.map(s => {
                const expanded = open[s.staffId] ?? (!admin || data.staff.length === 1);
                return (
                    <section key={s.staffId} className={card}>
                        <button type="button" className="flex w-full flex-wrap items-center justify-between gap-2 text-left" onClick={() => setOpen(o => ({ ...o, [s.staffId]: !expanded }))}>
                            <strong className="text-white">{s.staffName} <span className="text-xs font-normal text-slate-400">· {s.pages.length} page</span></strong>
                            <span className="flex flex-wrap gap-3 text-xs text-slate-300">
                                <span>👥 {fmt(s.followers)} ({sign(s.followers7)}/7 ngày)</span>
                                <span>📝 {s.posts30} bài</span>
                                <span>👁 {fmt(s.views30)}</span>
                                <span>💬 {fmt(s.interactions30)}</span>
                                <span className="text-slate-500">7 ngày tạo: {s.usage7.images} ảnh · {s.usage7.videos} video · {fmt(s.credits7)} credit</span>
                            </span>
                        </button>
                        {expanded && s.pages.length > 0 && (
                            <div className="mt-3 overflow-x-auto">
                                <table className="w-full text-left text-xs text-slate-300">
                                    <thead className="text-slate-500"><tr><th className="py-1">Page</th><th>Follow</th><th>7 ngày</th><th /><th className="text-right" title="Lượt xem toàn page 7 ngày (BM)">View page 7N</th><th className="text-right" title="Số người xem khác nhau 7 ngày (BM)">Tiếp cận 7N</th><th /><th className="text-right" title="Thời gian xem trung bình mỗi reel (BM)">Xem TB</th><th className="text-right">Bài 7/30 ngày</th><th className="text-right">View 30 ngày</th><th className="text-right">TB/bài</th><th className="pl-3">Bài tốt nhất</th><th className="text-right">Đăng gần nhất</th></tr></thead>
                                    <tbody>
                                        {s.pages.map(p => (
                                            <tr key={p.id} className="border-t border-slate-800">
                                                <td className="py-1.5">{p.url ? <a href={p.url} target="_blank" rel="noreferrer" className="font-semibold text-white hover:underline">{p.name}</a> : <span className="font-semibold text-white">{p.name}</span>}
                                                    <span className="ml-1 text-slate-500">{PAGE_STATUS[p.status]}</span>
                                                    {p.bm && <span className={`ml-1 ${p.syncError ? 'text-red-300' : 'text-sky-400'}`} title={p.syncError || `Đồng bộ BM lúc ${clock(p.syncedAt)}`}>{p.syncError ? '⚠️ BM' : '· BM'}</span>}</td>
                                                <td>{fmt(p.followers)}</td>
                                                <td className={p.followers7 > 0 ? 'text-emerald-300' : p.followers7 < 0 ? 'text-red-300' : ''}>{sign(p.followers7)}</td>
                                                <td><Sparkline values={p.history.map(h => h.followers)} title="Follow theo ngày" /></td>
                                                <td className="text-right">{p.bm ? fmt(p.pageViews7) : '—'}</td>
                                                <td className="text-right">{p.bm ? fmt(p.reach7) : '—'}</td>
                                                <td>{p.bm && <Sparkline values={p.daily.map(d => d.views || 0)} color="#38bdf8" title="Lượt xem page theo ngày (30 ngày)" />}</td>
                                                <td className="text-right">{p.avgWatchSec ? `${p.avgWatchSec}s` : '—'}</td>
                                                <td className="text-right">{p.posts7}/{p.posts30}</td>
                                                <td className="text-right">{fmt(p.views30)}</td>
                                                <td className="text-right">{p.posts30 ? fmt(Math.round(p.views30 / p.posts30)) : '—'}</td>
                                                <td className="pl-3">{p.best ? (p.best.url ? <a href={p.best.url} target="_blank" rel="noreferrer" className="hover:underline">{p.best.title || 'Bài'}</a> : p.best.title || 'Bài') : '—'}{p.best && <span className="text-slate-500"> ({fmt(p.best.views)})</span>}</td>
                                                <td className={`text-right ${p.status !== 'paused' && (p.quietDays == null || p.quietDays >= 2) ? 'text-amber-300' : ''}`}>{p.lastPost ? `${p.lastPost}${p.quietDays ? ` (${p.quietDays} ngày trước)` : ''}` : 'Chưa đăng'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>
                );
            })}
        </div>
    );
}

// ---------------- Trend hot ----------------

const modelLabel = m => (m === 'seedance_2.5' ? 'Video HD (đẹp)' : m ? 'Video nhanh' : '');
// Same order as server/fb-masters.js GROUPS; groups not listed sort last
const MASTER_GROUPS = ['Thú cưng & động vật', 'Chim & động vật hoang dã', 'Nông trại & sân vườn', 'Dọn dẹp & cải tạo nhà', 'Xây dựng & công trình', 'Decor & DIY', 'Miniature tí hon', 'Sinh vật khổng lồ & What-if', 'POV trải nghiệm', 'Hoài niệm & giấc mơ', 'ASMR & món ăn', 'Hài & hoạt hình', 'Du lịch & cảnh đẹp', 'Theo mùa / lễ', 'Khác'];
const groupMasters = masters => {
    const map = new Map();
    for (const m of masters) {
        const g = m.group || 'Khác';
        if (!map.has(g)) map.set(g, []);
        map.get(g).push(m);
    }
    const rank = g => (MASTER_GROUPS.includes(g) ? MASTER_GROUPS.indexOf(g) : 99);
    return [...map.entries()].sort((a, b) => rank(a[0]) - rank(b[0]));
};
const SOURCE_LABEL = { youtube: 'Shorts đang lên', google: 'Tìm kiếm đang bùng', team: 'Bài thắng của team', evergreen: 'Chủ đề bền' };

function TrendTab({ api, admin, onUseIdea }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState('');
    const [msg, setMsg] = useState(null);
    const [filter, setFilter] = useState('mine');
    const [ytKey, setYtKey] = useState('');
    const load = useCallback(() => api('trends').then(setData).catch(e => setError(e.message)), [api]);
    useEffect(() => { load(); }, [load]);

    const run = async (key, fn) => {
        setBusy(key);
        setMsg(null);
        try {
            await fn();
        } catch (e) {
            setMsg({ tone: 'error', text: e.message });
        } finally {
            setBusy('');
        }
    };
    const refresh = () => run('refresh', async () => {
        const r = await api('trends-refresh', {});
        setMsg({ tone: r.errors.length ? 'warn' : 'ok', text: `Đã cập nhật: ${r.ideas} ý tưởng cho ${r.niches} ngách, ${r.google} tìm kiếm Google dùng được.${r.errors.length ? ` Lưu ý: ${r.errors.join('; ')}` : ''}` });
        await load();
    });
    const saveKey = () => run('key', async () => {
        await api('admin-save', { youtubeApiKey: ytKey.trim() });
        setYtKey('');
        setMsg({ tone: 'ok', text: 'Đã lưu YouTube key — bấm "Cập nhật ngay" để lấy Shorts đang lên.' });
    });

    if (error) return <Notice tone="error">{error}</Notice>;
    if (!data) return <div className="grid place-items-center p-10 text-slate-400"><Loader2 className="animate-spin" /></div>;
    const t = data.trends;
    const mine = new Set(data.myNiches);
    const isMine = n => mine.has(n.toLowerCase()) || [...mine].some(m => m.includes(n.toLowerCase()) || n.toLowerCase().includes(m));
    const niches = t ? t.niches.filter(n => filter === 'all' || !mine.size || isMine(n.niche)) : [];

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-slate-400">
                    Cập nhật mỗi sáng 6h: Shorts đang tăng view nhanh theo ngách, tìm kiếm đang bùng ở Mỹ/Anh và bài thắng của team — AI lọc và gợi ý ý tưởng WOW.
                    {t && <span className="ml-1 text-slate-500">Lần cuối: {clock(t.updatedAt)}.</span>}
                </p>
                {mine.size > 0 && t && (
                    <div className="flex gap-1 text-xs">
                        {[['mine', 'Ngách của tôi'], ['all', 'Tất cả ngách']].map(([id, text]) => (
                            <button key={id} type="button" onClick={() => setFilter(id)} className={`rounded-full px-3 py-1 font-semibold ${filter === id ? 'bg-amber-400 text-slate-950' : 'bg-slate-800 text-slate-300'}`}>{text}</button>
                        ))}
                    </div>
                )}
            </div>
            {msg && <Notice tone={msg.tone} onClose={() => setMsg(null)}>{msg.text}</Notice>}

            {admin && (
                <section className={`${card} flex flex-wrap items-center gap-2`}>
                    <span className="text-sm text-slate-300">Admin:</span>
                    <button type="button" className={btnMain} disabled={Boolean(busy)} onClick={refresh}>{busy === 'refresh' ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Cập nhật ngay</button>
                    <input type="password" autoComplete="off" className={`${input} min-w-[220px] flex-1`} value={ytKey} onChange={e => setYtKey(e.target.value)}
                        placeholder={t?.youtube ? 'YouTube key đã lưu — dán key mới để thay' : 'YouTube Data API key (không bắt buộc, để lấy Shorts đang lên)'} />
                    <button type="button" className={btnGhost} disabled={Boolean(busy) || !ytKey.trim()} onClick={saveKey}><Save size={14} /> Lưu key</button>
                    {t?.errors?.length > 0 && <p className="w-full text-xs text-amber-300">Lần trước: {t.errors.join('; ')}</p>}
                </section>
            )}

            {!t && <Notice>Chưa có dữ liệu trend — {admin ? 'bấm "Cập nhật ngay".' : 'hệ thống sẽ cập nhật lúc 6h sáng.'}</Notice>}
            {t && !niches.length && <Notice>Chưa có ngách nào khớp page của bạn — điền "Ngách / chủ đề" cho page ở tab Page của tôi, hoặc xem "Tất cả ngách".</Notice>}

            {niches.map(n => (
                <section key={n.niche} className={card}>
                    <h3 className="mb-3 flex items-center gap-2 font-bold text-white"><Flame size={16} className="text-orange-400" /> {n.niche}</h3>
                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                        {n.ideas.map((idea, i) => (
                            <div key={i} className="flex flex-col rounded-lg border border-slate-700 bg-slate-950/60 p-3">
                                <span className="mb-1 w-fit rounded-full bg-orange-500/15 px-2 py-0.5 text-[11px] font-semibold text-orange-300">{SOURCE_LABEL[idea.source]}</span>
                                <b className="text-sm text-white">{idea.title}</b>
                                <p className="mt-1 text-sm text-slate-300">{idea.idea}</p>
                                <p className="mt-1 flex-1 text-xs text-slate-500">{idea.why}</p>
                                <button type="button" className={`${btnMain} mt-2 w-fit`} onClick={() => onUseIdea(n.niche, idea)}><Wand2 size={14} /> Dùng ý tưởng này</button>
                            </div>
                        ))}
                    </div>
                    {n.shorts.length > 0 && (
                        <>
                            <p className="mb-2 mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500">Shorts đang tăng nhanh (xem để học hook, không tải về)</p>
                            <div className="flex gap-3 overflow-x-auto pb-1">
                                {n.shorts.map(v => (
                                    <a key={v.id} href={v.url} target="_blank" rel="noreferrer" className="w-40 shrink-0 text-xs text-slate-300 hover:text-white">
                                        {v.thumb && <img src={v.thumb} alt="" className="mb-1 aspect-video w-full rounded object-cover" loading="lazy" />}
                                        <span className="line-clamp-2">{v.title}</span>
                                        <span className="text-slate-500">{fmt(v.views)} view · {fmt(v.vph)}/giờ</span>
                                    </a>
                                ))}
                            </div>
                        </>
                    )}
                </section>
            ))}

            {t && t.google.length > 0 && (
                <section className={card}>
                    <h3 className="mb-2 font-bold text-white">Đang được tìm nhiều ở Mỹ/Anh — mượn làm chủ đề</h3>
                    <p className="mb-2 text-xs text-slate-500">Chỉ giữ tìm kiếm mượn được về hình ảnh (mùa, lễ, con vật, hiện tượng…). Trend/lễ: đăng thử 1 bài trước, ăn khách mới làm thêm.</p>
                    <ul className="space-y-1.5 text-sm text-slate-300">
                        {t.google.map(g => (
                            <li key={g.title}>
                                <a href={g.url} target="_blank" rel="noreferrer" className="font-semibold text-white hover:underline">{g.title}</a>
                                <span className="text-xs text-slate-500"> · ~{fmt(g.traffic)} lượt tìm ({g.geos.join(', ')}){g.niches.length ? ` · hợp: ${g.niches.join(', ')}` : ''}</span>
                                {g.use && <span className="block text-xs text-slate-400">{g.use}</span>}
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {t && t.winners.length > 0 && (
                <section className={card}>
                    <h3 className="mb-2 flex items-center gap-2 font-bold text-white"><Trophy size={16} className="text-amber-300" /> Bài thắng của team tuần này</h3>
                    <ul className="space-y-1 text-sm text-slate-300">
                        {t.winners.map((w, i) => (
                            <li key={i}>
                                {w.url ? <a href={w.url} target="_blank" rel="noreferrer" className="font-semibold text-white hover:underline">{w.title || 'Bài'}</a> : <b className="text-white">{w.title || 'Bài'}</b>}
                                <span className="text-xs text-slate-500"> · {w.staffName} · {w.pageName}{w.niche ? ` (${w.niche})` : ''} · {fmt(w.views)} view</span>
                            </li>
                        ))}
                    </ul>
                    <p className="mt-2 text-xs text-slate-500">Xem prompt của bài thắng ở tab Bảng vàng.</p>
                </section>
            )}
        </div>
    );
}

// ---------------- Quản trị ----------------

/** Every page of the team: add a page for a staff member, rename it, hand it over, or delete it. */
function TeamPagesSection({ api, staff, masters, setMsg }) {
    const empty = { id: '', name: '', url: '', niche: '', staffId: '' };
    const [list, setList] = useState(null);
    const [form, setForm] = useState(empty);
    const [adding, setAdding] = useState(false);
    const [busy, setBusy] = useState('');
    const [suggesting, setSuggesting] = useState(false);
    const [scan, setScan] = useState(null);
    const [costMedian, setCostMedian] = useState(0);
    const byId = useMemo(() => new Map(masters.map(m => [m.id, m])), [masters]);
    const load = useCallback(() => api('admin-pages').then(r => {
        setList(r.pages);
        setScan(r.scan);
        setCostMedian(r.costMedian || 0);
        return r.pages;
    }).catch(e => setMsg({ tone: 'error', text: e.message })), [api, setMsg]);
    // Pages with no suggested master prompts (new from BM, or niche changed) get them from the AI in the background
    const fillMasters = useCallback(async () => {
        setSuggesting(true);
        try {
            for (let round = 0; round < 6; round++) {
                const r = await api('page-masters', {});
                if (!r.left || !r.done) break;
            }
            await load();
        } catch (e) {
            setMsg({ tone: 'error', text: e.message });
        } finally {
            setSuggesting(false);
        }
    }, [api, load, setMsg]);
    useEffect(() => {
        load().then(pages => {
            if (masters.length && pages?.some(p => p.masterAuto && !p.masterIds.some(id => byId.has(id)))) fillMasters();
        });
    }, [load]); // run once per mount; fillMasters reloads itself
    const run = async (action, body, okText, key = 'x') => {
        setBusy(key);
        try {
            await api(action, body);
            if (okText) setMsg({ tone: 'ok', text: okText });
            await load();
            return true;
        } catch (e) {
            setMsg({ tone: 'error', text: e.message });
            return false;
        } finally {
            setBusy('');
        }
    };
    const set = patch => setForm(f => ({ ...f, ...patch }));
    const [query, setQuery] = useState('');
    const [who, setWho] = useState('all'); // all | '' (unassigned) | staff id | ADMIN_OWNER | HIDDEN_OWNER
    const [sort, setSort] = useState('todo');
    const [picked, setPicked] = useState(() => new Set());
    const live = (list || []).filter(p => !p.hidden);
    const hiddenCount = (list || []).length - live.length;
    const unassigned = live.filter(p => !p.staffId);
    const owners = [{ id: ADMIN_OWNER, name: 'Admin (riêng)' }, ...staff];
    const chips = [
        { id: 'all', name: 'Tất cả', count: live.length },
        { id: '', name: 'Chưa giao', count: unassigned.length, warn: true },
        ...owners.map(s => ({ ...s, count: live.filter(p => p.staffId === s.id).length })),
        { id: HIDDEN_OWNER, name: 'Đã ẩn', count: hiddenCount },
        { id: BROKEN, name: '⚠️ Lỗi đồng bộ', count: (list || []).filter(isBroken).length, warn: true },
        { id: CM_ON, name: '💰 Đã bật CM', count: live.filter(p => p.cm?.on).length }
    ];
    // Pages that joined Content Monetization in the last 3 days — the admin hears it here instead of opening each Page
    const cmNew = live.filter(p => p.cm?.on && Date.now() - (p.cm.since || 0) < 3 * 86400000);
    const cmChecked = (list || []).reduce((a, p) => Math.max(a, p.cm?.checkedAt || 0), 0);
    const checkCm = async () => {
        setBusy('cm');
        try {
            const r = await api('admin-cm-check', {});
            setMsg({ tone: 'ok', text: `Đã kiểm tra CM ${r.checked} page${r.left ? ` (còn ${r.left} page, bấm lại để kiểm tra tiếp)` : ''}. ${r.on.length ? `Đã bật CM: ${r.on.join(', ')}.` : 'Chưa page nào bật CM.'}` });
            await load();
        } catch (e) {
            setMsg({ tone: 'error', text: e.message });
        } finally {
            setBusy('');
        }
    };
    const plain = text => String(text || '').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/gi, 'd').toLowerCase();
    const rows = useMemo(() => {
        const q = plain(query.trim());
        const sorters = {
            todo: (a, b) => (a.staffId ? 1 : 0) - (b.staffId ? 1 : 0) || a.name.localeCompare(b.name, 'vi'),
            name: (a, b) => a.name.localeCompare(b.name, 'vi'),
            follow: (a, b) => b.followers - a.followers,
            newest: (a, b) => b.createdAt - a.createdAt,
            waste: (a, b) => (b.made7 - b.posts7) - (a.made7 - a.posts7)
        };
        return (list || [])
            .filter(p => (who === BROKEN ? isBroken(p) : who === CM_ON ? !p.hidden && p.cm?.on : who === HIDDEN_OWNER ? p.hidden : !p.hidden && (who === 'all' || p.staffId === who)))
            .filter(p => !q || plain(`${p.name} ${p.niche} ${p.staffName}`).includes(q))
            .sort(sorters[sort]);
    }, [list, query, who, sort]);
    // a filter change drops picks that are no longer on screen, so a bulk action never touches a page the admin cannot see
    useEffect(() => setPicked(prev => new Set(rows.filter(p => prev.has(p.id)).map(p => p.id))), [rows]);
    const allPicked = rows.length > 0 && rows.every(p => picked.has(p.id));
    const togglePick = id => setPicked(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });
    const bulk = (body, okText) => run('admin-page-bulk', { ids: [...picked], ...body }, okText, 'bulk').then(ok => ok && setPicked(new Set()));
    const nameOf = id => (id === ADMIN_OWNER ? 'Admin (riêng)' : staff.find(s => s.id === id)?.name || 'Chưa giao');
    const removePages = async (pages, key) => {
        if (!window.confirm(pages.length === 1
            ? `Xoá hẳn "${pages[0].name}" và toàn bộ bài đã ghi? Page vẫn còn trong BM thì không xoá được (dùng Ẩn).`
            : `Xoá hẳn ${pages.length} page và toàn bộ bài đã ghi? Page nào vẫn còn trong BM sẽ được giữ lại (dùng Ẩn).`)) return;
        setBusy(key);
        try {
            const r = await api('admin-page-delete', { ids: pages.map(p => p.id) });
            setPicked(new Set());
            setMsg({ tone: 'ok', text: `Đã xoá ${r?.deleted ?? pages.length} page.${r?.kept?.length ? ` Giữ lại ${r.kept.join(', ')} vì vẫn còn trong BM (dùng Ẩn).` : ''}` });
            await load();
        } catch (e) {
            setMsg({ tone: 'error', text: e.message });
        } finally {
            setBusy('');
        }
    };
    const hidePage = p => window.confirm(`Ẩn "${p.name}" khỏi danh sách? Page sẽ không còn giao cho ai, không đồng bộ số liệu; khôi phục được ở mục "Đã ẩn".`)
        && run('admin-page-bulk', { ids: [p.id], staffId: HIDDEN_OWNER }, `Đã ẩn "${p.name}".`, p.id);
    const setPrimary = (p, id) => run('admin-page-save', { id: p.id, staffId: p.staffId, masterIds: [id, ...p.masterIds.filter(x => x !== id)].slice(0, 3) }, 'Đã đổi prompt nền.', p.id);

    return (
        <section className={card}>
            <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                <h3 className="font-bold text-white">Page của team & phân quyền</h3>
                <div className="flex flex-wrap gap-2">
                    <button type="button" className={btnGhost} disabled={!!busy} onClick={() => run('bm-scan', {}, '', 'scan').then(ok => ok && setMsg({ tone: 'ok', text: 'Đã quét BM.' }))}>
                        {busy === 'scan' ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Quét BM
                    </button>
                    <button type="button" className={btnGhost} disabled={!!busy} onClick={checkCm} title={cmChecked ? `Lần kiểm tra gần nhất: ${clock(cmChecked)}` : 'Chưa kiểm tra lần nào'}>
                        {busy === 'cm' ? <Loader2 size={14} className="animate-spin" /> : '💰'} Kiểm tra CM ngay
                    </button>
                    <button type="button" className={btnGhost} onClick={() => { setAdding(a => !a); setForm(empty); }}><Plus size={14} /> Thêm page ngoài BM</button>
                </div>
            </div>
            <p className="mb-3 text-xs text-slate-500">
                Page nào anh thêm vào BM (đã cấp cho System User) sẽ <b>tự xuất hiện ở đây</b> khi mở mục này và 4 lần mỗi ngày — chỉ cần chọn <b>người quản lý</b>.
                AI tự đoán ngách và gán <b>3 prompt nền</b> hợp nhất cho từng page; nhân sự <b>chỉ thấy đúng các prompt của page mình</b> (★ là prompt chính) — anh đặt prompt chính hoặc bấm × để bỏ prompt sai; page không còn prompt nào thì nhân sự mới thấy cả thư viện. Mỗi người chỉ thấy page, bài và insight của page mình. Page của anh chọn <b>Admin (giữ riêng)</b>: chỉ admin xem số liệu, không lên bảng xếp hạng, xu hướng hay nhóm Zalo.
                Tick nhiều page để giao, đặt video/ngày hoặc <b>ẩn</b> một lần; page BM bị ẩn (<EyeOff size={11} className="inline" />) thì không ai quản lý, không đồng bộ, khôi phục ở mục "Đã ẩn". Page đã gỡ quyền BM nằm ở mục "⚠️ Lỗi đồng bộ" — tick rồi bấm Xoá hẳn.
                Tool <b>tự kiểm tra kiếm tiền nội dung (CM)</b> mỗi 6 tiếng: page nào đã <b>chấp nhận lời mời</b> CM sẽ hiện nhãn 💰 và doanh thu 28 ngày (lọc ở mục "💰 Đã bật CM"); muốn biết ngay thì bấm <b>Kiểm tra CM ngay</b>. Lời mời chưa bấm chấp nhận thì Meta không cho tool thấy.
            </p>
            {cmNew.length > 0 && <Notice tone="ok">💰 <b>Page mới bật kiếm tiền (CM):</b> {cmNew.map(p => p.name).join(', ')} — <button type="button" className="underline" onClick={() => setWho(CM_ON)}>xem các page đã bật CM</button>.</Notice>}
            {scan?.error && <Notice tone="warn">Chưa quét được BM: {scan.error}</Notice>}
            {unassigned.length > 0 && who !== '' && (
                <Notice tone="warn"><b>{unassigned.length} page chưa giao</b> — <button type="button" className="underline" onClick={() => setWho('')}>lọc ra để giao</button> (tick nhiều page rồi giao một lần).</Notice>
            )}
            {suggesting && <p className="mb-2 flex items-center gap-1 text-xs text-sky-300"><Loader2 size={12} className="animate-spin" /> AI đang gợi ý prompt nền cho page mới…</p>}
            {(adding || form.id) && (
                <div className="mb-3 grid gap-2 rounded-lg border border-slate-800 p-3 md:grid-cols-5">
                    <input className={input} value={form.name} disabled={form.bm} onChange={e => set({ name: e.target.value })} placeholder="Tên page" />
                    <input className={input} value={form.url} disabled={form.bm} onChange={e => set({ url: e.target.value })} placeholder="https://facebook.com/..." />
                    <input className={input} value={form.niche} onChange={e => set({ niche: e.target.value })} placeholder="Ngách (để trống: AI tự đoán)" />
                    <select className={input} value={form.staffId} onChange={e => set({ staffId: e.target.value })}>
                        <option value="">— Giao cho —</option>
                        <option value={ADMIN_OWNER}>Admin (giữ riêng)</option>
                        {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                    <div className="flex gap-2">
                        <button type="button" className={btnMain} disabled={!!busy || !form.name.trim() || (!form.id && !form.staffId)} onClick={() => run('admin-page-save', form, form.id ? 'Đã cập nhật page.' : 'Đã thêm page.').then(ok => ok && (setForm(empty), setAdding(false)))}>
                            {form.id ? <Save size={15} /> : <Plus size={15} />} {form.id ? 'Lưu' : 'Thêm'}
                        </button>
                        <button type="button" className={btnGhost} onClick={() => { setForm(empty); setAdding(false); }}>Huỷ</button>
                    </div>
                </div>
            )}
            {list && (
                <>
                    <div className="mb-2 flex flex-wrap gap-1.5">
                        {chips.filter(c => c.count || c.id === 'all' || c.id === who).map(c => (
                            <button key={c.id || 'none'} type="button" onClick={() => setWho(c.id)}
                                className={`rounded-full border px-2.5 py-0.5 text-xs ${who === c.id ? 'border-amber-400 bg-amber-400/15 text-amber-200' : c.warn ? 'border-amber-400/40 text-amber-300 hover:bg-slate-800' : 'border-slate-700 text-slate-300 hover:bg-slate-800'}`}>
                                {c.name} <span className="text-slate-500">{c.count}</span>
                            </button>
                        ))}
                    </div>
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                        <label className="relative min-w-[220px] flex-1">
                            <Search size={14} className="pointer-events-none absolute left-2.5 top-2.5 text-slate-500" />
                            <input className={`${input} pl-8`} value={query} onChange={e => setQuery(e.target.value)} placeholder="Tìm theo tên page, ngách hoặc nhân sự…" />
                        </label>
                        <select className={`${input} w-auto`} value={sort} onChange={e => setSort(e.target.value)} title="Sắp xếp">
                            <option value="todo">Chưa giao lên đầu</option>
                            <option value="name">Tên A→Z</option>
                            <option value="follow">Nhiều follow nhất</option>
                            <option value="newest">Mới thêm nhất</option>
                            <option value="waste">Tạo nhiều mà ít đăng</option>
                        </select>
                        <span className="text-xs text-slate-500">{rows.length} page</span>
                    </div>
                    {picked.size > 0 && (
                        <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-amber-400/40 bg-amber-400/10 px-3 py-2 text-sm">
                            <b className="text-amber-200">Đã chọn {picked.size}</b>
                            {who === BROKEN ? (
                                <button type="button" className={`${btnGhost} text-red-300`} disabled={!!busy} onClick={() => removePages(rows.filter(p => picked.has(p.id)), 'bulk')}><Trash2 size={14} /> Xoá hẳn</button>
                            ) : who === HIDDEN_OWNER ? (
                                <button type="button" className={btnGhost} disabled={!!busy} onClick={() => bulk({ staffId: '' }, `Đã khôi phục ${picked.size} page — chọn người quản lý ở "Chưa giao".`)}><RotateCcw size={14} /> Khôi phục</button>
                            ) : (
                                <>
                                    <select className="rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-slate-100" value="" disabled={!!busy}
                                        onChange={e => e.target.value && bulk({ staffId: e.target.value === '-' ? '' : e.target.value }, `Đã giao ${picked.size} page cho ${e.target.value === '-' ? 'không ai' : nameOf(e.target.value)}.`)}>
                                        <option value="">Giao cho…</option>
                                        <option value={ADMIN_OWNER}>Admin (giữ riêng)</option>
                                        {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                        <option value="-">— Bỏ giao —</option>
                                    </select>
                                    <select className="rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-slate-100" value="x" disabled={!!busy}
                                        onChange={e => e.target.value !== 'x' && bulk({ dailyVideos: e.target.value }, `Đã đặt video/ngày cho ${picked.size} page.`)}>
                                        <option value="x">Video/ngày…</option>
                                        <option value="">Tự động</option>
                                        {[0, 1, 2, 3].map(n => <option key={n} value={n}>{n === 0 ? 'Dừng (0)' : `${n} video`}</option>)}
                                    </select>
                                    <button type="button" className={`${btnGhost} text-red-300`} disabled={!!busy}
                                        onClick={() => window.confirm(`Ẩn ${picked.size} page khỏi danh sách? Không ai quản lý, không đồng bộ số liệu; khôi phục được ở "Đã ẩn".`) && bulk({ staffId: HIDDEN_OWNER }, `Đã ẩn ${picked.size} page.`)}>
                                        <EyeOff size={14} /> Ẩn khỏi danh sách
                                    </button>
                                </>
                            )}
                            {busy === 'bulk' && <Loader2 size={14} className="animate-spin text-amber-300" />}
                            <button type="button" className="ml-auto text-xs text-slate-400 hover:underline" onClick={() => setPicked(new Set())}>Bỏ chọn</button>
                        </div>
                    )}
                <div className="max-h-[620px] overflow-auto rounded-lg border border-slate-800">
                    <table className="w-full min-w-[960px] text-left text-sm text-slate-200">
                        <thead className="sticky top-0 z-10 bg-slate-900 text-xs text-slate-500">
                            <tr>
                                <th className="w-8 px-2 py-1.5"><input type="checkbox" checked={allPicked} onChange={() => setPicked(allPicked ? new Set() : new Set(rows.map(p => p.id)))} title="Chọn tất cả page đang hiện" /></th>
                                <th>Page</th><th>Ngách</th><th>Prompt nền (AI gợi ý)</th><th>Video/ngày</th><th className="pl-3">Nhân sự</th><th />
                            </tr>
                        </thead>
                        <tbody>
                            {!rows.length && <tr><td colSpan={7} className="py-6 text-center text-xs text-slate-500">{query ? `Không có page nào khớp "${query}".` : 'Không có page nào ở mục này.'}</td></tr>}
                            {rows.map(p => (
                                <tr key={p.id} className={`border-t border-slate-800 align-top ${picked.has(p.id) ? 'bg-amber-400/10' : p.staffId ? '' : 'bg-amber-400/5'} ${p.hidden ? 'opacity-60' : ''}`}>
                                    <td className="px-2 py-2.5"><input type="checkbox" checked={picked.has(p.id)} onChange={() => togglePick(p.id)} /></td>
                                    <td className="py-2 pr-2">
                                        <span className="inline-flex items-center gap-2">
                                            {p.picture && <img src={p.picture} alt="" className="h-6 w-6 rounded-full" />}
                                            {p.url ? <a href={p.url} target="_blank" rel="noreferrer" className="hover:underline">{p.name}</a> : p.name}
                                        </span>
                                        <span className="block text-xs text-slate-500">{fmt(p.followers)} follow{p.bm && <span className="text-sky-400"> · BM</span>}{!p.staffId && <span className="text-amber-300"> · mới</span>}{p.syncError && <span className="text-red-300" title={p.syncError}> · lỗi đồng bộ</span>}{p.cm?.on && <span className="font-semibold text-emerald-300" title={`Bật CM từ ${clock(p.cm.since)} · kiểm tra ${clock(p.cm.checkedAt)}${p.cm.sample ? `
${p.cm.sample}` : ''}`}> · 💰 CM{cmMoney(p.cm) && ` ${cmMoney(p.cm)}/28 ngày`}</span>}</span>
                                    </td>
                                    <td className="pr-2">
                                        <input key={`${p.id}-${p.niche}`} className={`${input} py-1 text-xs`} defaultValue={p.niche} placeholder="AI tự đoán"
                                            onBlur={e => e.target.value.trim() !== p.niche && run('admin-page-save', { id: p.id, staffId: p.staffId, niche: e.target.value.trim() }, 'Đã lưu ngách — AI gợi ý lại prompt nền.', p.id)} />
                                        {p.nicheAuto && <span className="text-[11px] text-slate-500">AI đoán</span>}
                                    </td>
                                    <td className="max-w-[300px] pr-2">
                                        <div className="flex flex-wrap gap-1">
                                            {p.masterIds.filter(id => byId.has(id)).map((id, i) => (
                                                <span key={id} className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] ${i === 0 ? 'bg-amber-400/15 text-amber-200' : 'bg-slate-800 text-slate-300'}`} title={byId.get(id).use}>
                                                    {i === 0 ? '★ ' : ''}{byId.get(id).label || byId.get(id).name}
                                                    <button type="button" className="text-slate-500 hover:text-red-300" disabled={busy === p.id} title="Bỏ prompt này khỏi page (nhân sự không thấy nữa)"
                                                        onClick={() => run('admin-page-save', { id: p.id, staffId: p.staffId, masterIds: p.masterIds.filter(x => x !== id) }, 'Đã bỏ prompt khỏi page.', p.id)}><X size={11} /></button>
                                                </span>
                                            ))}
                                            {!p.masterIds.some(id => byId.has(id)) && <span className="text-xs text-slate-500">{suggesting ? 'đang gợi ý…' : 'chưa có'}</span>}
                                        </div>
                                        <div className="mt-1 flex gap-2">
                                            <select className="max-w-[170px] rounded bg-slate-950 px-1 text-[11px] text-slate-400" value="" disabled={busy === p.id} onChange={e => e.target.value && setPrimary(p, e.target.value)}>
                                                <option value="">+ Đặt prompt chính…</option>
                                                {groupMasters(masters).map(([group, items]) => (
                                                    <optgroup key={group} label={group}>{items.map(m => <option key={m.id} value={m.id}>{m.label || m.name}</option>)}</optgroup>
                                                ))}
                                            </select>
                                            <button type="button" className="text-[11px] text-sky-300 hover:underline" disabled={busy === p.id} onClick={() => run('page-masters', { ids: [p.id], force: true }, 'AI đã gợi ý lại.', p.id)}>{busy === p.id ? '…' : '↺ AI gợi ý lại'}</button>
                                        </div>
                                    </td>
                                    <td className="pr-2 text-xs">
                                        <select className="rounded bg-slate-950 px-1 py-1 text-slate-200" value={p.dailyVideos ?? ''} disabled={busy === p.id} title={p.plan.why}
                                            onChange={e => run('admin-page-save', { id: p.id, staffId: p.staffId, dailyVideos: e.target.value }, 'Đã đặt số video/ngày.', p.id)}>
                                            <option value="">Tự động · {p.plan.auto ? p.plan.target : '…'}</option>
                                            {[0, 1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n === 0 ? 'Dừng (0)' : `${n} video`}</option>)}
                                        </select>
                                        <span className="block text-[11px] text-slate-500">{p.plan.label}{p.plan.hours?.hours?.length ? ` · 🕒 ${p.plan.hours.hours.map(h => `${h}h`).join(' ')}` : ''}</span>
                                        <span className={`block text-[11px] ${p.made7 >= 3 && p.posts7 < p.made7 / 2 ? 'text-red-300' : 'text-slate-500'}`} title="Video đã tạo cho page trong 7 ngày / bài đã lên page (BM)">
                                            Tạo {p.made7} · đăng {p.posts7} (7N){p.made7 >= 3 && p.posts7 < p.made7 / 2 ? ' ⚠' : ''}
                                        </span>
                                    </td>
                                    <td className="pl-3">
                                        {p.hidden ? <span className="text-xs text-slate-500">Đã ẩn</span> : (
                                            <select className={`${input} py-1 ${p.staffId ? '' : 'border-amber-400/60'}`} value={p.staffId} disabled={busy === p.id} onChange={e => run('admin-page-save', { id: p.id, staffId: e.target.value }, e.target.value ? `Đã giao "${p.name}".` : `Đã bỏ giao "${p.name}".`, p.id)}>
                                                <option value="">— Chưa giao —</option>
                                                <option value={ADMIN_OWNER}>Admin (giữ riêng)</option>
                                                {p.staffId && p.staffId !== ADMIN_OWNER && !staff.some(s => s.id === p.staffId) && <option value={p.staffId}>{p.staffName}</option>}
                                                {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                            </select>
                                        )}
                                    </td>
                                    <td className="whitespace-nowrap px-2 text-right">
                                        {p.hidden ? (
                                            <button type="button" className="inline-flex items-center gap-1 px-1 text-xs text-sky-300 hover:underline" disabled={!!busy} onClick={() => run('admin-page-bulk', { ids: [p.id], staffId: '' }, `Đã khôi phục "${p.name}" — chọn người quản lý ở "Chưa giao".`, p.id)}><RotateCcw size={12} /> Khôi phục</button>
                                        ) : p.bm ? (
                                            <button type="button" className="px-1 text-slate-500 hover:text-red-300" disabled={!!busy} title="Ẩn khỏi danh sách (page vẫn ở BM)" onClick={() => hidePage(p)}><EyeOff size={14} /></button>
                                        ) : null}
                                        {isBroken(p) && (
                                            <button type="button" className="px-1 text-slate-500 hover:text-red-300" disabled={!!busy} title="Xoá hẳn (page đã mất quyền BM)" onClick={() => removePages([p], p.id)}><Trash2 size={14} /></button>
                                        )}
                                        {!p.bm && !p.hidden && (
                                            <>
                                                <button type="button" className="px-1 text-xs text-amber-300 hover:underline" onClick={() => setForm({ id: p.id, name: p.name, url: p.url, niche: p.niche, staffId: p.staffId, bm: p.bm })}>Sửa</button>
                                                <button type="button" className="px-1 text-slate-500 hover:text-red-300" disabled={!!busy} title="Xoá page và bài đã ghi" onClick={() => removePages([p], p.id)}><Trash2 size={14} /></button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                </>
            )}
            {list && <PageCost list={live} median={costMedian} busy={busy} onPause={p => run('admin-page-save', { id: p.id, staffId: p.staffId, dailyVideos: 0 }, `Đã dừng tạo video cho "${p.name}".`, p.id)} />}
        </section>
    );
}

/** Credits spent per page in 30 days vs the views and followers they brought; pages losing for weeks get a pause button. */
function PageCost({ list, median, busy, onPause }) {
    const [open, setOpen] = useState(false);
    const rows = list.filter(p => p.cost30?.credits > 0).sort((a, b) => (b.cost30.loss - a.cost30.loss) || b.cost30.credits - a.cost30.credits);
    const losing = rows.filter(p => p.cost30.loss);
    const total = rows.reduce((s, p) => s + p.cost30.credits, 0);
    if (!rows.length) return null;
    return (
        <div className="mt-4 rounded-lg border border-slate-800 p-3">
            <button type="button" className="flex w-full flex-wrap items-center justify-between gap-2 text-left" onClick={() => setOpen(o => !o)}>
                <strong className="text-sm text-white">Chi phí theo page · 30 ngày · ≈{fmt(total)} credit</strong>
                <span className="text-xs text-slate-400">{losing.length ? <b className="text-red-300">{losing.length} page nên dừng</b> : 'Chưa có page lỗ'} · {open ? 'Thu gọn' : 'Xem'}</span>
            </button>
            {open && (
                <>
                    <p className="mt-1 text-[11px] text-slate-500">
                        Credit là ước tính theo loại video (HD ≈ 8/giây, nhanh ≈ 4/giây, ảnh ≈ 5). "View/1K credit" = mỗi 1.000 credit mang về bao nhiêu view; trung bình team: <b>{fmt(median)}</b>.
                        Đề xuất dừng khi page đã chạy từ 3 tuần, tiêu từ 1.200 credit, không ở mức Đang nổ/Ổn định, follow tăng dưới 100 và view/credit dưới 1/4 mức team.
                    </p>
                    <div className="mt-2 max-h-[360px] overflow-auto">
                        <table className="w-full min-w-[640px] text-left text-sm text-slate-200">
                            <thead className="sticky top-0 bg-slate-900 text-xs text-slate-500"><tr><th className="py-1">Page</th><th className="text-right">Credit</th><th className="text-right">View</th><th className="text-right">Follow</th><th className="text-right">View/1K credit</th><th /></tr></thead>
                            <tbody>
                                {rows.map(p => (
                                    <tr key={p.id} className={`border-t border-slate-800 ${p.cost30.loss ? 'bg-red-500/5' : ''}`}>
                                        <td className="py-1">{p.name}<span className="block text-[11px] text-slate-500">{p.staffName || 'Chưa giao'} · {p.plan.label}</span></td>
                                        <td className="text-right">{fmt(p.cost30.credits)}</td>
                                        <td className="text-right">{fmt(p.cost30.views)}</td>
                                        <td className="text-right">{p.cost30.follows >= 0 ? '+' : ''}{fmt(p.cost30.follows)}</td>
                                        <td className={`text-right ${p.cost30.perK !== null && median && p.cost30.perK < median / 4 ? 'text-red-300' : ''}`}>{p.cost30.perK === null ? '—' : fmt(p.cost30.perK)}</td>
                                        <td className="pl-2 text-right">
                                            {p.cost30.loss && (
                                                <button type="button" className="rounded bg-red-500/20 px-2 py-0.5 text-xs font-semibold text-red-200 hover:bg-red-500/30" disabled={busy === p.id}
                                                    onClick={() => window.confirm(`Dừng tạo video cho "${p.name}"? (đặt 0 video/ngày, đổi lại được ở cột Video/ngày)`) && onPause(p)}>Dừng page</button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}

/** Meta BM: the System User token and a manual sync; the pages themselves are listed and assigned in the section above. */
function BmSection({ api, data, act, saving, setMsg }) {
    const [token, setToken] = useState('');
    const [busy, setBusy] = useState('');
    const syncAll = async () => {
        setBusy('sync');
        let total = 0;
        const errors = [];
        try {
            for (let round = 0; round < 10; round += 1) {
                const r = await api('bm-sync', { force: true });
                total += r.synced;
                errors.push(...(r.errors || []));
                if (!r.remaining) break;
            }
            setMsg({ tone: errors.length ? 'warn' : 'ok', text: `Đã đồng bộ ${total} page.${errors.length ? ` Lỗi: ${errors.slice(0, 5).join('; ')}` : ''}` });
        } catch (e) {
            setMsg({ tone: 'error', text: e.message });
        } finally {
            setBusy('');
        }
    };

    return (
        <section className={card}>
            <h3 className="mb-1 flex items-center gap-2 font-bold text-white"><Building2 size={16} /> Insight tự động qua Business Manager</h3>
            <p className="mb-3 text-xs text-slate-500">
                Trong BM: tạo <b>System User</b> (Admin) → gán các page (quyền xem insight / nội dung) và app Meta của team → tạo token
                với quyền <code>pages_show_list, pages_read_engagement, read_insights</code>, hạn <b>Không bao giờ</b>. Dán token vào đây — token chỉ lưu trên máy chủ.
                Page mới gán cho System User tự vào danh sách ở trên; số liệu tự đồng bộ lúc 9h, 13h, 17h, 21h và khi nhân sự mở tab Insight (nếu cũ hơn 2 giờ).
            </p>
            <p className="text-sm text-slate-300">
                {data.meta.configured
                    ? <>Đã kết nối: <b className="text-sky-300">{data.meta.name}</b> · thấy {fmt(data.meta.pages)} page</>
                    : 'Chưa kết nối BM — page vẫn dùng số liệu nhân sự nhập tay.'}
            </p>
            {data.meta.error && <p className="mt-1 text-xs text-red-300">Lỗi gần nhất ({clock(data.meta.errorAt)}): {data.meta.error}</p>}
            <div className="mt-3 flex flex-wrap gap-2">
                <input type="password" autoComplete="off" className={`${input} min-w-[260px] flex-1`} value={token} onChange={e => setToken(e.target.value)} placeholder={data.meta.configured ? 'Dán token mới để thay' : 'Token System User'} />
                <button type="button" className={btnMain} disabled={saving || !token.trim()} onClick={() => act('admin-save', { metaToken: token.trim() }, 'Đã kết nối BM.', () => setToken(''))}><Save size={15} /> Lưu token</button>
                {data.meta.configured && <button type="button" className={btnGhost} disabled={saving} onClick={() => window.confirm('Ngắt kết nối BM? Số liệu đã đồng bộ vẫn giữ.') && act('admin-save', { clearMetaToken: true }, 'Đã ngắt kết nối BM.')}>Ngắt</button>}
                {data.meta.configured && <button type="button" className={btnGhost} disabled={Boolean(busy)} onClick={syncAll}>{busy === 'sync' ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Đồng bộ tất cả ngay</button>}
            </div>
        </section>
    );
}

/** Admin-only summary above the per-person list: team totals, people ranked by 30-day views, and pages that need a look. */
function TeamOverview({ staff }) {
    const pages = staff.flatMap(s => s.pages.map(p => ({ ...p, staffName: s.staffName })));
    const total = key => staff.reduce((sum, s) => sum + (s[key] || 0), 0);
    const ranked = [...staff].sort((a, b) => b.views30 - a.views30);
    const quiet = pages.filter(p => p.status !== 'paused' && (p.quietDays == null || p.quietDays >= 3));
    const stars = [...pages].filter(p => p.posts7 > 0).sort((a, b) => b.views7 / b.posts7 - a.views7 / a.posts7).slice(0, 3);
    return (
        <section className={card}>
            <h3 className="mb-3 font-bold text-white">Tổng quan team</h3>
            <div className="grid grid-cols-2 gap-2 text-center sm:grid-cols-5">
                {[['Nhân sự', staff.length], ['Page', pages.length], ['Bài 30N', total('posts30')], ['View 30N', fmt(total('views30'))], ['Credit 7N', fmt(total('credits7'))]].map(([k, v]) => (
                    <div key={k} className="rounded-lg bg-slate-950 px-2 py-2"><div className="text-lg font-black text-white">{v}</div><div className="text-[11px] text-slate-400">{k}</div></div>
                ))}
            </div>
            <div className="mt-3 grid gap-3 md:grid-cols-3">
                <div>
                    <div className="mb-1 text-xs font-semibold text-slate-400">Xếp hạng view 30 ngày</div>
                    {ranked.map((s, i) => <div key={s.staffId} className="flex justify-between text-sm text-slate-200"><span>{i + 1}. {s.staffName}</span><span className="text-slate-400">{fmt(s.views30)} · {fmt(s.credits7)} cr</span></div>)}
                </div>
                <div>
                    <div className="mb-1 text-xs font-semibold text-emerald-300">Page đang tốt (view TB/bài 7N)</div>
                    {stars.length === 0 && <div className="text-sm text-slate-500">Chưa có bài 7 ngày qua.</div>}
                    {stars.map(p => <div key={p.id} className="text-sm text-slate-200">{p.name} <span className="text-slate-400">· {p.staffName} · {fmt(Math.round(p.views7 / p.posts7))}</span></div>)}
                </div>
                <div>
                    <div className="mb-1 text-xs font-semibold text-amber-300">Cần nhắc (ngưng đăng ≥ 3 ngày)</div>
                    {quiet.length === 0 && <div className="text-sm text-slate-500">Không có.</div>}
                    {quiet.slice(0, 8).map(p => <div key={p.id} className="text-sm text-slate-200">{p.name} <span className="text-slate-400">· {p.staffName} · {p.quietDays == null ? 'chưa đăng' : `${p.quietDays} ngày`}</span></div>)}
                </div>
            </div>
        </section>
    );
}

function AdminTab({ api, refreshMe }) {
    const [data, setData] = useState(null);
    const [form, setForm] = useState(null);
    const [key, setKey] = useState('');
    const [prompt, setPrompt] = useState({ id: '', name: '', niche: '', text: '' });
    const [msg, setMsg] = useState(null);
    const [saving, setSaving] = useState(false);
    const [zaloPreview, setZaloPreview] = useState('');
    const [labeling, setLabeling] = useState('');
    const [aiKey, setAiKey] = useState('');
    const [hookSecret, setHookSecret] = useState('');
    const [models, setModels] = useState([]);
    const load = useCallback(() => api('admin-get').then(d => {
        setData(d);
        setForm({ limits: d.limits, staffLimits: d.staffLimits, videoModels: d.videoModels, allow30: d.allow30, hdHotOnly: d.hdHotOnly, planRetry: d.planRetry, aiModel: d.ai.overrideModel, slots: d.slots, zalo: { enabled: d.zalo.enabled, daily: d.zalo.daily, events: d.zalo.events, quietDays: d.zalo.quietDays, weakViews: d.zalo.weakViews, minStaff: d.zalo.minStaff } });
    }).catch(e => setMsg({ tone: 'error', text: e.message })), [api]);
    useEffect(() => { load(); }, [load]);
    const configured = Boolean(data?.ai.configured);
    useEffect(() => {
        if (configured) api('admin-ai-models').then(r => setModels(r.models)).catch(() => setModels([]));
    }, [api, configured, data?.ai.source]);
    const visibleMasters = useMemo(() => (data?.masters || []).filter(m => !m.hidden), [data]);
    if (!data || !form) return msg ? <Notice tone={msg.tone}>{msg.text}</Notice> : <Loader2 className="animate-spin text-slate-400" />;

    const act = async (action, body, okText, after) => {
        setSaving(true);
        setMsg(null);
        try {
            const r = await api(action, body);
            setMsg({ tone: 'ok', text: okText });
            after?.(r);
            load();
            refreshMe();
        } catch (e) {
            setMsg({ tone: 'error', text: e.message });
        } finally {
            setSaving(false);
        }
    };
    const usageOf = id => data.usage.find(u => u.staffId === id) || {};
    const setLimit = (staffId, kind, value) => setForm(f => {
        if (!staffId) return { ...f, limits: { ...f.limits, [kind]: value } };
        const own = { ...(f.staffLimits[staffId] || f.limits), [kind]: value };
        return { ...f, staffLimits: { ...f.staffLimits, [staffId]: own } };
    });
    const clearOverride = staffId => setForm(f => { const next = { ...f.staffLimits }; delete next[staffId]; return { ...f, staffLimits: next }; });
    // AI names/groups the prompts 18 at a time until none are left (all = relabel everything, e.g. after a big import)
    const labelMasters = async all => {
        const since = all ? Date.now() : 0;
        setMsg(null);
        let done = 0;
        try {
            for (let round = 0; round < 15; round++) {
                setLabeling(`Đang phân loại… (${done} xong)`);
                const r = await api('admin-prompt-label', { since });
                done += r.done;
                if (!r.left || !r.done) break;
            }
            setMsg({ tone: 'ok', text: `AI đã đặt tên & phân nhóm ${done} master prompt. Đang gợi ý lại prompt nền cho các page…` });
            // names/groups changed, so refresh every page's suggested masters (24 pages per call)
            const redoFrom = Date.now();
            for (let round = 0; round < 6; round++) {
                const r = await api('page-masters', { since: redoFrom });
                if (!r.left || !r.done) break;
            }
            setMsg({ tone: 'ok', text: `AI đã đặt tên & phân nhóm ${done} master prompt và gợi ý lại prompt nền cho các page.` });
        } catch (e) {
            setMsg({ tone: 'error', text: `${e.message} (đã xong ${done})` });
        }
        setLabeling('');
        load();
        refreshMe();
    };
    const loadFile = e => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        file.text().then(text => setPrompt(p => ({ ...p, text, name: p.name || file.name.replace(/\.[^.]+$/, '') })));
    };
    // Many files at once: "Ngách — Tên.txt" fills the niche; a file whose name matches an existing master replaces its text.
    const importFiles = async e => {
        const files = [...(e.target.files || [])];
        e.target.value = '';
        if (!files.length) return;
        setSaving(true);
        setMsg(null);
        let done = 0;
        const failed = [];
        for (const file of files) {
            const base = file.name.replace(/\.[^.]+$/, '').trim();
            const [niche, name] = base.includes(' — ') ? base.split(' — ', 2).map(s => s.trim()) : ['', base];
            const old = data.masters.find(m => m.name.toLowerCase() === name.toLowerCase());
            try {
                const text = await file.text();
                await api('admin-prompt', { id: old?.id || '', name, niche: niche || old?.niche || '', text });
                done++;
            } catch (err) {
                failed.push(`${name}: ${err.message}`);
            }
        }
        setSaving(false);
        setMsg({ tone: failed.length ? 'error' : 'ok', text: `Đã nhập ${done}/${files.length} master prompt.${failed.length ? ` Lỗi: ${failed.join('; ')}` : ''}` });
        load();
        refreshMe();
    };

    return (
        <div className="space-y-4">
            {msg && <Notice tone={msg.tone} onClose={() => setMsg(null)}>{msg.text}</Notice>}
            <MaintenanceCard current={data.maintenance} act={act} saving={saving} />
            {data.gencpu && <GencpuCard g={data.gencpu} act={act} saving={saving} />}
            {data.muse && <MuseCard m={data.muse} act={act} saving={saving} />}
            <div className="grid gap-4 lg:grid-cols-2">
                <section className={card}>
                    <h3 className="mb-2 flex items-center gap-2 font-bold text-white"><KeyRound size={16} /> Seedvis</h3>
                    <p className="text-sm text-slate-300">{data.seedvis.configured ? <>Đã có key <code className="text-slate-400">{data.seedvis.masked}</code> · Số dư: <b className="text-amber-300">{data.seedvis.balance == null ? '—' : fmt(data.seedvis.balance)}</b> credit</> : 'Chưa có key — nhân sự chưa tạo được ảnh/video.'}</p>
                    {data.seedvis.balanceError && <p className="mt-1 text-xs text-red-300">{data.seedvis.balanceError}</p>}
                    <div className="mt-3 flex gap-2">
                        <input type="password" autoComplete="off" className={input} value={key} onChange={e => setKey(e.target.value)} placeholder={data.seedvis.configured ? 'Nhập key mới để thay' : 'Seedvis API key'} />
                        <button type="button" className={btnMain} disabled={saving || !key.trim()} onClick={() => act('admin-save', { seedvisApiKey: key.trim() }, 'Đã lưu key Seedvis.', () => setKey(''))}><Save size={15} /> Lưu</button>
                    </div>
                    <p className="mt-2 text-xs text-slate-500">Key chỉ lưu trên máy chủ (server_config), nhân sự không bao giờ thấy.</p>
                    <div className="mt-4 border-t border-slate-800 pt-3">
                        <h4 className="text-sm font-semibold text-white">Webhook — chạy tiếp khi nhân sự đóng tab</h4>
                        <p className="mt-1 text-xs text-slate-400">Trong trang Seedvis → <b>Cấu hình API Key & Webhook</b>: dán URL dưới đây, lưu, rồi chép <b>Signing secret</b> (chỉ hiện 1 lần) vào ô bên dưới. Khi có webhook, video xong sẽ tự báo và job xếp hàng tự chạy mà không cần ai mở tab.</p>
                        <div className="mt-2 flex gap-2">
                            <code className="min-w-0 flex-1 truncate rounded-lg bg-slate-950 px-2 py-2 text-xs text-slate-300">{data.hook?.url}</code>
                            <button type="button" className={btnGhost} onClick={() => navigator.clipboard?.writeText(data.hook?.url || '').then(() => setMsg({ tone: 'ok', text: 'Đã chép URL webhook.' }))}><Copy size={14} /></button>
                        </div>
                        <div className="mt-2 flex gap-2">
                            <input type="password" autoComplete="off" className={input} value={hookSecret} onChange={e => setHookSecret(e.target.value)} placeholder={data.hook?.configured ? 'Đã có secret — nhập mới để thay' : 'Signing secret từ Seedvis'} />
                            <button type="button" className={btnMain} disabled={saving || !hookSecret.trim()} onClick={() => act('admin-save', { seedvisHookSecret: hookSecret.trim() }, 'Đã lưu signing secret webhook.', () => setHookSecret(''))}><Save size={15} /> Lưu</button>
                            {data.hook?.configured && <button type="button" className={btnGhost} disabled={saving} onClick={() => window.confirm('Tắt webhook? Job xếp hàng sẽ chỉ chạy khi nhân sự mở tab.') && act('admin-save', { clearSeedvisHook: true }, 'Đã tắt webhook.')}>Tắt</button>}
                        </div>
                        <p className="mt-1 text-xs">
                            {!data.hook?.configured ? <span className="text-amber-300">Chưa bật — job xếp hàng chỉ chạy khi nhân sự mở tab.</span>
                                : data.hook.error && (!data.hook.at || data.hook.errorAt > data.hook.at) ? <span className="text-red-300">Lần nhận gần nhất bị từ chối ({new Date(data.hook.errorAt).toLocaleString('vi-VN')}): {data.hook.error} — kiểm tra lại secret.</span>
                                    : data.hook.at ? <span className="text-emerald-300">Đang hoạt động · nhận lần cuối {new Date(data.hook.at).toLocaleString('vi-VN')}</span>
                                        : <span className="text-slate-400">Đã lưu secret — chờ Seedvis gửi sự kiện đầu tiên (tạo thử 1 ảnh).</span>}
                        </p>
                    </div>
                </section>
                <section className={card}>
                    <h3 className="mb-2 flex items-center gap-2 font-bold text-white"><Bot size={16} /> Model</h3>
                    <p className="text-sm text-slate-300">AI (Beeknoee): {data.ai.source === 'fb'
                        ? <>key riêng FB <code className="text-slate-400">{data.ai.masked}</code></>
                        : data.ai.source === 'hopelab' ? <>đang mượn key HopeLab</> : <span className="text-red-300">chưa có key</span>} · model <code className="text-slate-400">{data.ai.model}</code></p>
                    <div className="mt-2 flex gap-2">
                        <input type="password" autoComplete="off" className={input} value={aiKey} onChange={e => setAiKey(e.target.value)} placeholder={data.ai.source === 'fb' ? 'Nhập key mới để thay' : 'Beeknoee API key riêng cho FB'} />
                        <button type="button" className={btnMain} disabled={saving || !aiKey.trim()} onClick={() => act('admin-save', { fbAiApiKey: aiKey.trim(), aiModel: form.aiModel }, 'Đã lưu key AI riêng cho FB.', () => setAiKey(''))}><Save size={15} /> Lưu</button>
                        {data.ai.source === 'fb' && <button type="button" className={btnGhost} disabled={saving} onClick={() => window.confirm('Bỏ key riêng, quay lại dùng key HopeLab?') && act('admin-save', { clearFbAiKey: true }, 'Đã bỏ key riêng — dùng key HopeLab.')}>Bỏ</button>}
                    </div>
                    <label className="mt-2 block"><span className={label}>Model AI cho FB Studio (để trống = theo HopeLab) — bấm "Lưu hạn mức & model" bên dưới</span>
                        <input className={input} list="fb-ai-models" value={form.aiModel} onChange={e => setForm(f => ({ ...f, aiModel: e.target.value }))} placeholder="VD: gemini-2.5-flash" />
                        <datalist id="fb-ai-models">{models.map(m => <option key={m} value={m} />)}</datalist></label>
                    {models.length > 0 && <p className="mt-1 text-[11px] text-slate-500">Key này dùng được {models.length} model — gõ để lọc.</p>}
                    <p className="mt-1 text-[11px] text-slate-500">Key riêng giúp tách chi phí AI của FB khỏi HopeLab. Khi lưu, hệ thống thử 1 lượt nhỏ với model đang chọn; key chỉ lưu trên máy chủ.</p>
                    <div className="mt-3 flex flex-wrap gap-4 text-sm text-slate-200">
                        {['seedance_2.5', 'seedance_2.0_fast'].map(m => (
                            <label key={m} className="flex items-center gap-2"><input type="checkbox" checked={form.videoModels.includes(m)} onChange={e => setForm(f => ({ ...f, videoModels: e.target.checked ? [...f.videoModels, m] : f.videoModels.filter(x => x !== m) }))} /> {m}</label>
                        ))}
                        <label className="flex items-center gap-2"><input type="checkbox" checked={form.allow30} onChange={e => setForm(f => ({ ...f, allow30: e.target.checked }))} /> Cho phép video 30s (Seedance 2.5)</label>
                        <label className="flex items-center gap-2" title="Page khác chỉ dùng video nhanh, tối đa 15s; admin không bị giới hạn"><input type="checkbox" checked={form.hdHotOnly} onChange={e => setForm(f => ({ ...f, hdHotOnly: e.target.checked }))} /> HD & 30s chỉ cho page "Đang nổ"</label>
                        <label className="flex items-center gap-2" title="Mỗi bài trong kế hoạch được tạo video tối đa bao nhiêu lần (tính cả làm lại khi chưa ưng)">Lượt tạo mỗi bài
                            <select className="rounded bg-slate-950 px-1 py-0.5" value={form.planRetry} onChange={e => setForm(f => ({ ...f, planRetry: Number(e.target.value) }))}>
                                {[1, 2, 3].map(n => <option key={n} value={n}>{n}</option>)}
                            </select></label>
                    </div>
                </section>
            </div>

            <TeamPagesSection api={api} staff={data.staff} masters={visibleMasters} setMsg={setMsg} />
            <BmSection api={api} data={data} act={act} saving={saving} setMsg={setMsg} />

            <section className={card}>
                <h3 className="mb-2 font-bold text-white">Hạn mức mỗi ngày (reset 0h giờ VN)</h3>
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm text-slate-200">
                        <thead className="text-xs text-slate-500"><tr><th className="py-1">Nhân sự</th>{Object.values(KIND_LABEL).map(t => <th key={t}>{t}</th>)}<th>Đã dùng hôm nay</th><th /></tr></thead>
                        <tbody>
                            <tr className="border-t border-slate-800 bg-slate-950/50">
                                <td className="py-1.5 font-semibold">Mặc định cả team</td>
                                {Object.keys(KIND_LABEL).map(k => <td key={k}><input type="number" min="0" className={`${input} w-20`} value={form.limits[k]} onChange={e => setLimit('', k, e.target.value)} /></td>)}
                                <td /><td />
                            </tr>
                            {data.staff.map(s => {
                                const own = form.staffLimits[s.id];
                                const u = usageOf(s.id);
                                return (
                                    <tr key={s.id} className="border-t border-slate-800">
                                        <td className="py-1.5">{s.name}{s.accessStatus && s.accessStatus !== 'approved' && <span className="ml-1 text-xs text-red-300">(tạm ngưng)</span>}</td>
                                        {Object.keys(KIND_LABEL).map(k => <td key={k}><input type="number" min="0" className={`${input} w-20 ${own ? '' : 'opacity-50'}`} value={(own || form.limits)[k]} onChange={e => setLimit(s.id, k, e.target.value)} /></td>)}
                                        <td className="text-xs text-slate-400">{fmt(u.images)} ảnh · {fmt(u.videos)} video · {fmt(u.ai)} AI</td>
                                        <td>{own && <button type="button" className="text-xs text-slate-400 hover:text-white" onClick={() => clearOverride(s.id)}>Về mặc định</button>}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <p className="mt-2 text-xs text-slate-500">
                    Chỉ tính ảnh/video Seedvis trả về hợp lệ — job lỗi, bị từ chối, chưa máy nào nhận sau 40 phút hoặc đang làm mà quá 3 giờ thì tự hoàn lượt.
                    Mức mặc định tốn tối đa {fmt(form.limits.images * data.credits.images + form.limits.videos * data.credits.videos)} credit/người/ngày ({data.credits.images}/ảnh, {data.credits.videos}/video)
                    · cả team ({data.staff.length} người) tối đa ~{fmt(data.staff.reduce((sum, s) => { const l = form.staffLimits[s.id] || form.limits; return sum + l.images * data.credits.images + l.videos * data.credits.videos; }, 0))} credit/ngày nếu dùng hết.
                </p>
                <div className="mt-3 flex flex-wrap items-end gap-4 rounded-lg bg-slate-950/60 p-3 text-sm text-slate-200">
                    <label><span className={label}>Job Seedvis cùng lúc / người</span><input type="number" min="1" max="16" className={`${input} w-24`} value={form.slots.perStaff} onChange={e => setForm(f => ({ ...f, slots: { ...f.slots, perStaff: e.target.value } }))} /></label>
                    <label><span className={label}>Tối đa cả team (gói Seedvis)</span><input type="number" min="1" max="64" className={`${input} w-24`} value={form.slots.total} onChange={e => setForm(f => ({ ...f, slots: { ...f.slots, total: e.target.value } }))} /></label>
                    <p className="flex-1 text-xs text-slate-500">Đang chạy: <b className="text-amber-300">{data.activeJobs}</b>/{data.slots.total}. Job vượt mức nằm trong hàng chờ HopeTeam và tự gửi khi có slot (chờ quá 12h thì huỷ, hoàn lượt).</p>
                </div>
                <button type="button" className={`${btnMain} mt-3`} disabled={saving} onClick={() => act('admin-save', form, 'Đã lưu cài đặt.')}><Save size={15} /> Lưu hạn mức & model</button>
            </section>

            <section className={card}>
                <h3 className="mb-1 flex items-center gap-2 font-bold text-white"><BellRing size={16} /> Nhắc việc group Zalo</h3>
                <p className="mb-3 text-xs text-slate-500">
                    Group chung của team nên bot chỉ nhắn việc chung, không nhắn từng bài/từng page.
                    <br /><b>Sáng 9h</b> (chỉ gửi khi có việc): nhắc khi page ngưng đăng thuộc từ <b>{form.zalo.minStaff}</b> người trở lên, kèm tin vui lớn từ hôm trước (bài vượt 100K view, page bật kiếm tiền, page vượt 10K follow) và cảnh báo hết credit.
                    <br /><b>Thứ Hai</b>: báo cáo tuần — tổng bài/view/follow so tuần trước, xếp hạng người, page hiệu quả / cần đổi chủ đề, prompt nền tốt nhất, ai tạo nhiều đăng ít, giờ đăng tốt.
                </p>
                {!data.zalo.ready && <Notice tone="warn">Máy chủ chưa có ZALO_BOT_TOKEN / ZALO_GROUP_ID — nhắc việc sẽ không gửi được.</Notice>}
                <div className="mt-2 flex flex-wrap items-end gap-4 text-sm text-slate-200">
                    <label className="flex items-center gap-2"><input type="checkbox" checked={form.zalo.enabled} onChange={e => setForm(f => ({ ...f, zalo: { ...f.zalo, enabled: e.target.checked } }))} /> Bật bot Zalo (báo cáo tuần)</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={form.zalo.daily} onChange={e => setForm(f => ({ ...f, zalo: { ...f.zalo, daily: e.target.checked } }))} /> Tin sáng hằng ngày</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={form.zalo.events} onChange={e => setForm(f => ({ ...f, zalo: { ...f.zalo, events: e.target.checked } }))} /> Kèm tin vui nổi bật trong tin sáng</label>
                    <label><span className={label}>Nhắc ngưng đăng khi từ (người)</span><input type="number" min="1" max="20" className={`${input} w-28`} value={form.zalo.minStaff} onChange={e => setForm(f => ({ ...f, zalo: { ...f.zalo, minStaff: e.target.value } }))} /></label>
                    <label><span className={label}>Nhắc khi page ngưng đăng (ngày)</span><input type="number" min="1" max="14" className={`${input} w-28`} value={form.zalo.quietDays} onChange={e => setForm(f => ({ ...f, zalo: { ...f.zalo, quietDays: e.target.value } }))} /></label>
                    <label><span className={label}>Dưới mức này = chưa hiệu quả (view TB/bài)</span><input type="number" min="0" className={`${input} w-36`} value={form.zalo.weakViews} onChange={e => setForm(f => ({ ...f, zalo: { ...f.zalo, weakViews: e.target.value } }))} /></label>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                    <button type="button" className={btnMain} disabled={saving} onClick={() => act('admin-save', { zalo: form.zalo }, 'Đã lưu cài đặt Zalo.')}><Save size={15} /> Lưu</button>
                    <button type="button" className={btnGhost} disabled={saving} onClick={() => api('admin-zalo', {}).then(r => setZaloPreview(r.text)).catch(e => setMsg({ tone: 'error', text: e.message }))}>Xem trước tin sáng + báo cáo tuần</button>
                    <button type="button" className={btnGhost} disabled={saving || !data.zalo.ready} onClick={() => window.confirm('Gửi tin nhắc ngay vào group Zalo?') && act('admin-zalo', { send: true }, 'Đã gửi vào group Zalo.')}>Gửi ngay</button>
                </div>
                {zaloPreview && <pre className="mt-3 whitespace-pre-wrap rounded-lg bg-slate-950 p-3 text-xs text-slate-300">{zaloPreview}</pre>}
            </section>

            <section className={card}>
                <h3 className="mb-1 font-bold text-white">Master prompt (ẩn với nhân sự)</h3>
                <p className="mb-3 text-xs text-slate-500">Nhân sự chỉ thấy <b>tên tiếng Việt + nhóm + mô tả "hợp với"</b> (không thấy tên file, nội dung). Prompt bị ẩn (vi phạm luật hoặc admin ẩn) không hiện cho nhân sự. AI đọc nội dung ở máy chủ khi gợi ý ý tưởng, viết prompt và caption.</p>
                <div className="mb-3 flex flex-wrap items-center gap-2">
                    <button type="button" className={btnMain} disabled={saving || !!labeling || !data.masters.some(m => !m.labeled)} onClick={() => labelMasters(false)}>
                        {labeling ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} AI đặt tên & phân nhóm ({data.masters.filter(m => !m.labeled).length} chưa có)
                    </button>
                    <button type="button" className={btnGhost} disabled={saving || !!labeling || !data.masters.length} onClick={() => window.confirm('Cho AI đặt tên & phân nhóm lại toàn bộ?') && labelMasters(true)}>Làm lại tất cả</button>
                    <span className="text-xs text-slate-400">{labeling || `${data.masters.filter(m => !m.hidden).length} hiện · ${data.masters.filter(m => m.hidden).length} ẩn`}</span>
                </div>
                <div className="mb-3 max-h-[480px] space-y-3 overflow-y-auto pr-1">
                    {data.masters.length === 0 && <p className="text-sm text-slate-500">Chưa có master prompt.</p>}
                    {groupMasters(data.masters.map(m => ({ ...m, group: m.labeled ? m.group : 'Chưa phân loại' }))).map(([group, list]) => (
                        <div key={group}>
                            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-sky-300">{group} ({list.length})</div>
                            <div className="space-y-1.5">
                    {list.map(m => (
                        <div key={m.id} className={`flex items-start justify-between gap-2 rounded-lg bg-slate-950 px-3 py-2 text-sm ${m.hidden ? 'text-slate-500' : 'text-slate-200'}`}>
                            <span className="min-w-0">
                                <b className={m.hidden ? 'line-through' : ''}>{m.label || m.name}</b>
                                {m.hidden && <span className="ml-2 rounded bg-red-500/15 px-1.5 py-0.5 text-[11px] text-red-300">Ẩn · {m.reason || 'admin ẩn'}</span>}
                                <span className="block text-xs text-slate-500">{m.label ? `File: ${m.name} · ` : ''}{fmt(m.chars)} ký tự</span>
                                {m.use && <span className="block text-xs text-slate-400">{m.use}</span>}
                            </span>
                            <span className="flex shrink-0 gap-2">
                                <button type="button" className="text-xs text-sky-300 hover:underline" disabled={saving} onClick={() => act('admin-prompt', { id: m.id, hidden: !m.hidden }, m.hidden ? 'Đã hiện lại cho nhân sự.' : 'Đã ẩn với nhân sự.')}>{m.hidden ? 'Hiện' : 'Ẩn'}</button>
                                <button type="button" className="text-xs text-amber-300 hover:underline" onClick={() => setPrompt({ id: m.id, name: m.name, niche: m.niche, text: '' })}>Thay nội dung</button>
                                <button type="button" className="text-slate-500 hover:text-red-300" onClick={() => window.confirm(`Xoá "${m.name}"?`) && act('admin-prompt', { id: m.id, delete: true }, 'Đã xoá.')}><Trash2 size={14} /></button>
                            </span>
                        </div>
                    ))}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="grid gap-2 md:grid-cols-2">
                    <input className={input} value={prompt.name} onChange={e => setPrompt(p => ({ ...p, name: e.target.value }))} placeholder="Tên hiển thị cho nhân sự" />
                    <input className={input} value={prompt.niche} onChange={e => setPrompt(p => ({ ...p, niche: e.target.value }))} placeholder="Ngách (VD: chim làm tổ)" />
                    <textarea className={`${input} min-h-[160px] font-mono text-xs md:col-span-2`} value={prompt.text} onChange={e => setPrompt(p => ({ ...p, text: e.target.value }))} placeholder="Dán nội dung master prompt, hoặc chọn file .txt/.md" />
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                    <label className={`${btnGhost} cursor-pointer`}><Upload size={14} /> Chọn file<input type="file" accept=".txt,.md,text/plain,text/markdown" className="hidden" onChange={loadFile} /></label>
                    <label className={`${btnGhost} cursor-pointer ${saving ? 'pointer-events-none opacity-50' : ''}`} title='Chọn nhiều file .txt cùng lúc. Tên file dạng "Ngách — Tên prompt.txt"; trùng tên thì thay nội dung.'><Upload size={14} /> Nhập nhiều file<input type="file" multiple accept=".txt,.md,text/plain,text/markdown" className="hidden" onChange={importFiles} /></label>
                    <button type="button" className={btnMain} disabled={saving || !prompt.name.trim() || prompt.text.trim().length < 50} onClick={() => act('admin-prompt', prompt, prompt.id ? 'Đã cập nhật master prompt.' : 'Đã thêm master prompt.', () => setPrompt({ id: '', name: '', niche: '', text: '' }))}>
                        <Save size={15} /> {prompt.id ? 'Cập nhật' : 'Thêm'}
                    </button>
                    {prompt.id && <button type="button" className={btnGhost} onClick={() => setPrompt({ id: '', name: '', niche: '', text: '' })}>Huỷ sửa</button>}
                </div>
            </section>
        </div>
    );
}

// ---------------- Shell ----------------

const EMPTY_DRAFT = {
    pageId: '', masterId: '', niche: '', notes: '', idea: '', title: '', seconds: 15, imagePrompt: '', videoPrompt: '', feedback: '',
    imageRatio: '9:16', videoRatio: '9:16', refUrl: '', startImage: '', model: '', since: 0
};

/** Ding + system notification + tab title badge when jobs finish, so staff can work elsewhere while Seedvis renders. */
function announceFinished(list, pushOn) {
    const done = list.filter(g => g.state === 'done').length;
    const failed = list.length - done;
    const text = [done ? `${done} ảnh/video đã xong — tải về ngay` : '', failed ? `${failed} job lỗi (đã hoàn lượt)` : ''].filter(Boolean).join(' · ');
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        [880, 1320].forEach((freq, i) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            const at = ctx.currentTime + i * 0.15;
            osc.frequency.value = freq;
            gain.gain.setValueAtTime(0.15, at);
            gain.gain.exponentialRampToValueAtTime(0.001, at + 0.3);
            osc.connect(gain).connect(ctx.destination);
            osc.start(at);
            osc.stop(at + 0.3);
        });
        setTimeout(() => ctx.close(), 1000);
    } catch (_) { /* sound is optional */ }
    // With server push on, the service worker already shows it (same tag), so only a browser without push shows its own
    if (!pushOn && document.hidden) showLocal('FB Studio', text, 'fb-studio-done');
    if (document.hidden) {
        const match = document.title.match(/^\((\d+)\) ✅ (.*)$/);
        const original = match ? match[2] : document.title;
        document.title = `(${(match ? Number(match[1]) : 0) + list.length}) ✅ ${original}`;
        const reset = () => {
            if (document.hidden) return;
            document.title = original;
            document.removeEventListener('visibilitychange', reset);
        };
        document.addEventListener('visibilitychange', reset);
    }
}

/** Banner for everyone while the admin has paused video and/or image making (Seedvis maintenance). */
function MaintenanceNotice({ m, admin }) {
    if (!m?.video && !m?.image) return null;
    const what = m.video && m.image ? 'tạo ảnh và video' : m.video ? 'tạo video' : 'tạo ảnh';
    return (
        <Notice tone="warn">
            🛠️ <b>Đang bảo trì {what}</b>{m.since ? ` từ ${clock(m.since)}` : ''}{m.note ? ` — ${m.note}` : ''}. Bài đang xếp hàng được giữ chỗ và tự chạy khi mở lại; trong lúc chờ vẫn soạn prompt, caption và đăng bài đã có.
            {admin && ' (Tắt ở tab Quản trị → Bảo trì.)'}
        </Notice>
    );
}

/** Admin: one-click pause of video / image making while the provider maintains its models. */
const PROVIDERS = [
    ['seedvis', 'Chỉ Seedvis (như cũ)'],
    ['auto', 'Seedvis, tự chuyển GenCPU khi lỗi/bảo trì'],
    ['parallel', 'Song song: GenCPU nhận khi còn luồng trống'],
    ['gencpu', 'GenCPU cho mọi video']
];

function GencpuCard({ g, act, saving }) {
    const [key, setKey] = useState('');
    const i = g.info;
    return (
        <section className={card}>
            <h3 className="mb-1 flex items-center gap-2 font-bold text-white"><Film size={16} /> GenCPU — máy tạo video dự phòng</h3>
            <p className="mb-2 text-xs text-slate-500">gencpu.com cũng tạo Seedance 2.0/2.5 (~1.000đ/video). Chế độ "song song": GenCPU nhận video mỗi khi còn luồng trống, còn lại chạy Seedvis — cả hai cùng chạy nên ra video nhanh hơn. Chế độ "tự chuyển": Seedvis bảo trì (nút bảo trì video đang bật), hết credit, lỗi máy chủ hoặc video lỗi → video tự gửi qua GenCPU. Video Seedvis chạy chậm thì vẫn chờ Seedvis (Seedvis không hủy được job, chuyển sang sẽ ra 2 video trùng), nhân sự vẫn tạo bình thường. Bị từ chối vì nội dung thì không chuyển. Ảnh vẫn tạo ở Seedvis.</p>
            <p className="text-sm text-slate-300">{g.configured ? <>Đã có key <code className="text-slate-400">{g.masked}</code>{i && <> · Gói <b className="text-amber-300">{i.plan || '?'}</b> · {i.threads} luồng · đã dùng {fmt(i.used)}/{i.limit ? fmt(i.limit) : '∞'}</>}</> : 'Chưa có key — chế độ dự phòng chưa chạy.'}</p>
            {g.error && <p className="mt-1 text-xs text-red-300">{g.error}</p>}
            <div className="mt-3 flex gap-2">
                <input type="password" autoComplete="off" className={input} value={key} onChange={e => setKey(e.target.value)} placeholder={g.configured ? 'Nhập key mới để thay' : 'GenCPU API key (sk_live_…)'} />
                <button type="button" className={btnMain} disabled={saving || !key.trim()} onClick={() => act('admin-save', { gencpuApiKey: key.trim() }, 'Đã lưu key GenCPU.', () => setKey(''))}><Save size={15} /> Lưu</button>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
                {PROVIDERS.map(([v, label]) => (
                    <button key={v} type="button" className={g.provider === v ? btnMain : btnGhost} disabled={saving || (v !== 'seedvis' && !g.configured)} onClick={() => act('admin-save', { videoProvider: v }, `Đã chọn: ${label}.`)}>{label}</button>
                ))}
                <select className={input + ' w-auto'} value={g.threads} disabled={saving} title="Số video FB Studio gửi GenCPU cùng lúc. GenCPU tự xếp hàng theo số luồng của gói và dùng chung với Viral Studio: để 2 thì luồng không bị trống giữa 2 video mà Viral Studio vẫn được chen vào." onChange={e => act('admin-save', { gencpuThreads: Number(e.target.value) }, 'Đã đổi số video gửi GenCPU cùng lúc.')}>
                    {[1, 2, 3, 4, 5, 6, 8, 10].map(n => <option key={n} value={n}>gửi {n} video/lần</option>)}
                </select>
                <select className={input + ' w-auto'} value={g.resolution} disabled={saving} onChange={e => act('admin-save', { gencpuResolution: Number(e.target.value) }, 'Đã đổi độ phân giải GenCPU.')}>
                    {[480, 720, 1080].map(r => <option key={r} value={r}>{r}p</option>)}
                </select>
                <label className="flex items-center gap-2 text-sm text-slate-300" title="Seedvis báo bảo trì Seedance 2.5: video 2.5 (20–30s, chỉ page Đang nổ) chạy qua GenCPU (~2.000đ/video 30s), chưa có key thì tạm khoá 2.5. Bỏ tick khi Seedvis mở lại 2.5 → 2.5 quay về Seedvis.">
                    <input type="checkbox" checked={g.seedvis25Down} disabled={saving} onChange={e => act('admin-save', { seedvis25Down: e.target.checked }, e.target.checked ? 'Seedvis 2.5 bảo trì: video 2.5 chạy qua GenCPU.' : 'Video 2.5 quay về Seedvis.')} /> Seedvis 2.5 đang bảo trì → 2.5 (30s) qua GenCPU
                </label>
                <label className="flex items-center gap-2 text-sm text-amber-200" title="Gói GenCPU (VD 650K): mọi video chạy qua GenCPU (0đ), nhân sự không bị trừ lượt video/ngày, không gửi sang Muse. Video nhanh (Seedance 2.0, ≤15s) không giới hạn; HD 30s (Seedance 2.5) chỉ cho page Đang nổ, theo phần lượt/ngày bên cạnh. Máy anh bật Viral Studio thì video FB tự chạy tiếp mỗi phút dù không ai mở tab. Ảnh vẫn tạo ở Seedvis.">
                    <input type="checkbox" checked={g.unlimited} disabled={saving || !g.configured} onChange={e => act('admin-save', { gencpuUnlimited: e.target.checked }, e.target.checked ? 'Bật GenCPU không giới hạn: video không trừ lượt, chạy hết qua GenCPU.' : 'Tắt GenCPU không giới hạn: video quay về chế độ cũ và hạn mức ngày.')} /> 🔓 GenCPU không giới hạn (nhân sự tạo video thoải mái)
                </label>
                {g.unlimited && (
                    <select className={input + ' w-auto'} value={g.share25} disabled={saving} title="Gói GenCPU có số lượt Seedance 2.5 mỗi ngày chung cho cả Viral Studio và FB Studio (650K = 15, 2L = 30, 3L = 45…). Đây là phần của FB; ở Viral Studio đặt 'Lượt Seedance 2.5/ngày' = tổng gói trừ số này. Hết lượt thì FB chỉ tạo video nhanh ≤15s, 0h mở lại." onChange={e => act('admin-save', { gencpu25Daily: Number(e.target.value) }, 'Đã đổi phần lượt 2.5 của FB.')}>
                        {[0, 1, 2, 3, 4, 5, 6, 8, 10, 15].map(n => <option key={n} value={n}>FB: {n} lượt 2.5/ngày</option>)}
                    </select>
                )}
                {g.unlimited && <span className="text-xs text-slate-400">2.5 hôm nay: {g.used25}/{g.share25}</span>}
                {g.configured && <button type="button" className={btnGhost} disabled={saving} onClick={() => window.confirm('Xoá key GenCPU? Video sẽ chỉ chạy qua Seedvis.') && act('admin-save', { clearGencpuKey: true, videoProvider: 'seedvis' }, 'Đã xoá key GenCPU.')}>Xoá key</button>}
            </div>
        </section>
    );
}

function MuseCard({ m, act, saving }) {
    const [token, setToken] = useState('');
    const [words, setWords] = useState(m.longWords);
    const online = m.seenAt && Date.now() - m.seenAt < 3 * 60 * 1000;
    const mb = (m.stored?.bytes || 0) / 1024 / 1024;
    return (
        <section className={card}>
            <h3 className="mb-1 flex items-center gap-2 font-bold text-white"><Film size={16} /> Muse — dự phòng cuối (chạy trên máy anh)</h3>
            <p className="mb-2 text-xs text-slate-500">Chỉ khi Seedvis bảo trì video hoặc GenCPU kín luồng, video mới được gửi cho Viral Studio trên máy anh tạo bằng Muse rồi upload lên Vercel Blob. Máy tắt hay Muse tắt → không gửi gì, video đã gửi mà không ai nhận trong vài phút thì tự quay về hàng chờ cũ. Video ≤15s ra clip 10s; 20–30s chỉ cho page có ngách chứa từ khoá bên dưới (ghép nhiều clip 10s). File tự xoá sau 3 ngày — nhân sự nhớ tải về trước. Gói Vercel Hobby miễn phí: vượt hạn mức thì Blob bị khoá chứ không tính tiền, không cần thẻ.</p>
            <p className="text-sm text-slate-300">
                {m.configured ? <>Máy Muse: {online ? <b className="text-emerald-300">{m.alive ? 'đang bật' : 'online, Muse tắt'}</b> : <b className="text-slate-400">offline</b>}{m.seenAt ? <span className="text-slate-500"> (lần cuối {new Date(m.seenAt).toLocaleTimeString('vi-VN')})</span> : null} · Hôm nay {m.today}/{m.max} video · Đang lưu {m.stored?.files || 0} file ({mb.toFixed(0)} MB / 1.000 MB)</> : 'Chưa tạo mã kết nối.'}
                {!m.blob && <span className="text-red-300"> · Chưa thấy Blob store trên Vercel</span>}
            </p>
            {token && (
                <div className="mt-2 rounded-lg border border-amber-400/50 bg-amber-400/10 p-2 text-xs text-amber-100">
                    Mã kết nối (chỉ hiện 1 lần) — dán vào Viral Studio → Cài đặt → Muse → "Mã kết nối FB Studio":
                    <div className="mt-1 flex gap-2"><code className="break-all text-white">{token}</code><button type="button" className={btnGhost} onClick={() => navigator.clipboard?.writeText(token)}>Copy</button></div>
                </div>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-2">
                <label className="flex items-center gap-2 text-sm text-slate-300">
                    <input type="checkbox" checked={m.on} disabled={saving || !m.configured} onChange={e => act('admin-save', { museRelayOn: e.target.checked }, e.target.checked ? 'Đã bật Muse dự phòng.' : 'Đã tắt Muse dự phòng.')} /> Bật Muse dự phòng
                </label>
                <select className={input + ' w-auto'} value={m.max} disabled={saving} title="Tối đa số video Muse mỗi ngày (giữ Blob miễn phí)" onChange={e => act('admin-save', { museDailyMax: Number(e.target.value) }, 'Đã đổi giới hạn Muse/ngày.')}>
                    {[5, 10, 20, 30, 40, 60].map(n => <option key={n} value={n}>{n} video/ngày</option>)}
                </select>
                <button type="button" className={btnGhost} disabled={saving} onClick={() => (!m.configured || window.confirm('Tạo mã mới? Mã cũ trong Viral Studio sẽ hết dùng được.')) && act('admin-save', { newMuseToken: true }, 'Đã tạo mã kết nối — copy ngay, mã chỉ hiện 1 lần.', r => setToken(r?.museToken || ''))}>{m.configured ? 'Tạo mã mới' : 'Tạo mã kết nối'}</button>
                <button type="button" className={btnGhost} disabled={saving} onClick={() => act('admin-muse-check', {}, 'Vercel Blob kết nối tốt.')}>Kiểm tra Blob</button>
            </div>
            <div className="mt-3 flex gap-2">
                <input className={input} value={words} maxLength={300} onChange={e => setWords(e.target.value)} placeholder="Ngách cho video 20–30s, cách nhau dấu phẩy (VD: chim, động vật, phong cảnh)" />
                <button type="button" className={btnMain} disabled={saving || words.trim() === m.longWords} onClick={() => act('admin-save', { museLongWords: words.trim() }, 'Đã lưu ngách video dài.')}><Save size={15} /> Lưu</button>
            </div>
        </section>
    );
}

function MaintenanceCard({ current, act, saving }) {
    const m = current || { video: false, image: false, note: '' };
    const [note, setNote] = useState(m.note || '');
    const on = m.video || m.image;
    const save = (patch, okText) => act('admin-save', { maintenance: { video: m.video, image: m.image, note: note.trim(), ...patch } }, okText);
    return (
        <section className={`${card} ${on ? 'border-amber-400/60' : ''}`}>
            <h3 className="mb-1 flex items-center gap-2 font-bold text-white">🛠️ Bảo trì máy tạo ảnh/video {on && <span className="rounded-full bg-amber-400/20 px-2 py-0.5 text-xs text-amber-200">ĐANG BẬT</span>}</h3>
            <p className="mb-2 text-xs text-slate-500">Khi nhà cung cấp (Seedvis) bảo trì model: bật để nhân sự thấy thông báo, nút tạo bị khoá (không mất lượt), bài đang xếp hàng được giữ lại chờ và tự chạy khi anh tắt; tin Zalo buổi sáng không nhắc "page ngưng đăng".</p>
            <input className={input} value={note} maxLength={300} onChange={e => setNote(e.target.value)} placeholder="Lời nhắn cho nhân sự (VD: dự kiến mở lại chiều nay)" />
            <div className="mt-2 flex flex-wrap gap-2">
                <button type="button" className={m.video ? btnMain : btnGhost} disabled={saving} onClick={() => save({ video: !m.video }, m.video ? 'Đã mở lại tạo video.' : 'Đã tạm dừng tạo video.')}>
                    <Film size={14} /> {m.video ? 'Mở lại tạo video' : 'Tạm dừng tạo video'}
                </button>
                <button type="button" className={m.image ? btnMain : btnGhost} disabled={saving} onClick={() => save({ image: !m.image }, m.image ? 'Đã mở lại tạo ảnh.' : 'Đã tạm dừng tạo ảnh.')}>
                    <ImageIcon size={14} /> {m.image ? 'Mở lại tạo ảnh' : 'Tạm dừng tạo ảnh'}
                </button>
                {on && <button type="button" className={btnGhost} disabled={saving || note.trim() === (m.note || '')} onClick={() => save({}, 'Đã cập nhật lời nhắn.')}><Save size={14} /> Lưu lời nhắn</button>}
            </div>
        </section>
    );
}

export default function FbStudio({ firebaseUser }) {
    const api = useApi(firebaseUser);
    const [tab, setTab] = useState(() => (window.location.pathname.split('/')[2] || 'create'));
    const [me, setMe] = useState(null);
    const [error, setError] = useState('');
    const [gens, setGens] = useState([]);
    const [draft, setDraft] = useState(() => {
        try {
            return { ...EMPTY_DRAFT, ...JSON.parse(localStorage.getItem('fbStudioDraft') || '{}'), since: 0 };
        } catch (_) {
            return EMPTY_DRAFT;
        }
    });

    const refreshMe = useCallback(() => api('me').then(r => { setMe(r); setError(''); }).catch(e => setError(e.message)), [api]);
    useEffect(() => {
        if (!firebaseUser) return;
        refreshMe();
        api('generations').then(r => setGens(r.generations)).catch(() => {});
    }, [firebaseUser, api, refreshMe]);
    useEffect(() => { localStorage.setItem('fbStudioDraft', JSON.stringify({ ...draft, since: 0 })); }, [draft]);
    useEffect(() => {
        if (me && !draft.model && me.options.videoModels[0]) setDraft(d => ({ ...d, model: me.options.videoModels[0] }));
    }, [me, draft.model]);
    // A staff member with one page starts on it (niche + its best master prompt); with several, the first niche is filled in
    useEffect(() => {
        const pages = me?.myPages || [];
        if (pages.length === 1) {
            const p = pages[0];
            const first = p.masterIds.find(id => me.masters.some(m => m.id === id));
            setDraft(d => (d.pageId ? d : { ...d, pageId: p.id, niche: p.niche || d.niche, masterId: d.masterId || first || '' }));
        } else if (me?.myNiches?.length) setDraft(d => (d.niche ? d : { ...d, niche: me.myNiches[0] }));
    }, [me]);
    useEffect(() => {
        const path = tab === 'create' ? '/fb' : `/fb/${tab}`;
        if (window.location.pathname !== path) window.history.replaceState({}, '', path);
    }, [tab]);

    // Pending jobs: ask the server every 10 s (it checks Seedvis and refunds the quota when a job fails)
    const pendingIds = useMemo(() => gens.filter(g => g.state === 'pending' || g.state === 'queued').map(g => g.id).join(','), [gens]);
    const gensRef = useRef(gens);
    gensRef.current = gens;
    const [toasts, setToasts] = useState([]);
    const [notify, setNotify] = useState(notifyState);
    const [pushOn, setPushOn] = useState(false);
    const pushOnRef = useRef(false);
    pushOnRef.current = pushOn;
    const [iosHelp, setIosHelp] = useState(false);
    // Notifications come from the server, so they arrive with the tab closed or the phone in another app
    useEffect(() => {
        addManifest();
        if (!firebaseUser) return undefined;
        if (notifyState() === 'granted') enablePush(api, { ask: false }).then(s => setPushOn(s === 'granted')).catch(() => {});
        else ensureWorker();
        const onMessage = e => { if (e.data?.type === 'fb-open') setTab('library'); };
        navigator.serviceWorker?.addEventListener('message', onMessage);
        return () => navigator.serviceWorker?.removeEventListener('message', onMessage);
    }, [firebaseUser, api]);
    const turnOnNotify = () => enablePush(api)
        .then(s => {
            setNotify(s);
            setPushOn(s === 'granted');
            if (s === 'granted') api('push-test', {}).catch(() => {});
        })
        .catch(e => window.alert(`Chưa bật được thông báo: ${e.message}`));
    useEffect(() => {
        if (!pendingIds) return undefined;
        const timer = setInterval(() => {
            api('poll', { ids: pendingIds.split(',') })
                .then(r => {
                    const before = gensRef.current;
                    const finished = r.generations.filter(x => (x.state === 'done' || x.state === 'failed')
                        && before.some(g => g.id === x.id && (g.state === 'pending' || g.state === 'queued')));
                    setGens(list => list.map(g => r.generations.find(x => x.id === g.id) || g));
                    if (finished.length) {
                        refreshMe();
                        announceFinished(finished, pushOnRef.current);
                        setToasts(list => [...finished, ...list].slice(0, 4));
                    }
                })
                .catch(() => {});
        }, 10000);
        return () => clearInterval(timer);
    }, [pendingIds, api, refreshMe]);
    // Without the server webhook, waiting jobs only move while this tab polls, so warn before closing it
    const hasQueued = gens.some(g => g.state === 'queued') && me?.options.background === false;
    useEffect(() => {
        if (!hasQueued) return undefined;
        const warn = e => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [hasQueued]);

    const fixGeneration = g => {
        setDraft(d => ({ ...d, ...fixPatch(g, d) }));
        setTab('create');
    };
    const useGeneration = g => {
        setDraft(d => ({ ...d, ...reusePatch(g, d) }));
        setTab('create');
    };
    const videoFromImage = g => {
        setDraft(d => ({ ...d, startImage: g.urls[0], pageId: d.pageId || g.pageId || '', title: d.title || g.title || '' }));
        setTab('create');
    };

    if (!firebaseUser) return <div className="grid place-items-center gap-2 p-10 text-slate-400"><Loader2 className="animate-spin" /><span className="text-sm">Đang khôi phục phiên đăng nhập…</span></div>;
    if (error && !me) return <div className="mx-auto max-w-xl space-y-3 p-6"><Notice tone="error">{error}</Notice><button type="button" className={btnGhost} onClick={refreshMe}><RefreshCw size={14} /> Thử lại</button></div>;
    if (!me) return <div className="grid place-items-center p-10 text-slate-400"><Loader2 className="animate-spin" /></div>;

    const tabs = TABS.filter(t => !t.admin || me.role === 'admin');
    return (
        <div className={`mx-auto max-w-[1500px] space-y-4 text-slate-100 ${gens.some(isRunning) ? 'pb-28' : ''}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="hidden sm:block"><FbLogo subtitle="Nuôi page bằng nội dung AI — tạo, QA, tải về, đăng tay, học từ bài thắng." /></div>
                <div className="flex flex-wrap gap-2">
                    {notify === 'default' && (
                        <button type="button" onClick={turnOnNotify} className="inline-flex items-center gap-1 rounded-full border border-sky-500/50 px-3 py-1 text-xs font-semibold text-sky-300 hover:bg-sky-500/10" title="Báo lên điện thoại/máy tính khi ảnh/video xong, kể cả khi đã đóng tab hoặc đang dùng app khác.">
                            <Bell size={12} /> Bật thông báo khi xong
                        </button>
                    )}
                    {notify === 'ios-install' && (
                        <button type="button" onClick={() => setIosHelp(v => !v)} className="inline-flex items-center gap-1 rounded-full border border-sky-500/50 px-3 py-1 text-xs font-semibold text-sky-300 hover:bg-sky-500/10">
                            <Bell size={12} /> Nhận thông báo trên iPhone
                        </button>
                    )}
                    {notify === 'denied' && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-slate-700 px-3 py-1 text-xs text-slate-400" title="Bạn đã chặn thông báo của trang này. Bấm biểu tượng ổ khoá cạnh địa chỉ web → Thông báo → Cho phép, rồi tải lại trang.">
                            <Bell size={12} /> Thông báo đang bị chặn
                        </span>
                    )}
                    {notify === 'granted' && (
                        <button type="button" onClick={() => api('push-test', {}).then(r => (r.sent ? null : turnOnNotify())).catch(() => turnOnNotify())} className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 px-3 py-1 text-xs font-semibold text-emerald-300 hover:bg-emerald-500/10" title="Đã bật — bấm để gửi thử 1 thông báo">
                            <BellRing size={12} /> Thông báo: bật
                        </button>
                    )}
                    {Object.entries(KIND_LABEL).map(([k, t]) => {
                        const left = Math.max(0, me.limits[k] - (me.usage[k] || 0));
                        const pending = me.pending?.[k] || 0;
                        return <span key={k} title="Chỉ tính lượt tạo thành công. Lượt lỗi/bị từ chối được hoàn lại tự động." className={`rounded-full border px-3 py-1 text-xs font-semibold ${left ? 'border-slate-700 text-slate-200' : 'border-red-500/40 text-red-300'}`}>{t}: còn {left}/{me.limits[k]}{pending ? ` (${pending} đang tạo)` : ''}</span>;
                    })}
                    <span className="w-full text-right text-[11px] text-slate-500">Chỉ tính kết quả hợp lệ — lỗi tự hoàn lượt · chạy tối đa {me.slots.perStaff} job cùng lúc{me.queued ? `, ${me.queued} job đang xếp hàng` : ''} · {me.options.background ? 'đóng tab/chuyển app vẫn chạy tiếp' : 'giữ tab mở khi có job xếp hàng'}</span>
                </div>
            </div>
            {iosHelp && (
                <Notice onClose={() => setIosHelp(false)}>
                    iPhone chỉ nhận thông báo khi mở FB Studio từ <b>màn hình chính</b> (iOS 16.4 trở lên): 1) mở hopeteam.xyz/fb bằng <b>Safari</b> → 2) bấm nút <b>Chia sẻ</b> (ô vuông có mũi tên lên) → <b>Thêm vào MH chính</b> → 3) mở FB Studio từ biểu tượng mới, đăng nhập, bấm <b>Bật thông báo khi xong</b>.
                </Notice>
            )}
            <MaintenanceNotice m={me.options.maintenance} admin={me.role === 'admin'} />
            {(!me.ready.seedvis || !me.ready.ai) && (
                <Notice tone="warn">
                    {!me.ready.seedvis && 'Admin chưa cấu hình máy tạo ảnh/video. '}
                    {!me.ready.ai && 'Chưa có API AI nên chưa dùng được trợ lý. '}
                    {me.role === 'admin' ? 'Vào tab Quản trị để cài.' : 'Báo admin để cài.'}
                </Notice>
            )}
            <nav className="flex gap-1 overflow-x-auto border-b border-slate-800">
                {tabs.map(t => {
                    const Icon = t.icon;
                    return (
                        <button key={t.id} type="button" onClick={() => setTab(t.id)}
                            className={`flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-semibold ${tab === t.id ? 'border-amber-400 text-amber-200' : 'border-transparent text-slate-400 hover:text-slate-200'}`}>
                            <Icon size={15} /> {t.label}
                        </button>
                    );
                })}
            </nav>
            <RunningBar gens={gens} onOpen={() => setTab('library')} />
            {toasts.length > 0 && (
                <div className={`fixed left-4 right-4 z-50 space-y-2 sm:left-auto sm:w-80 ${gens.some(isRunning) ? 'bottom-28' : 'bottom-4'}`}>
                    {toasts.map(g => (
                        <div key={g.id} className={`rounded-xl border bg-slate-900 p-3 text-sm shadow-xl ${g.state === 'done' ? 'border-emerald-500/50 text-emerald-200' : 'border-red-500/50 text-red-200'}`}>
                            <div className="flex items-start justify-between gap-2">
                                <span>{g.state === 'done' ? '✅' : '⚠️'} {g.kind === 'video' ? 'Video' : 'Ảnh'} {g.title ? `"${g.title}" ` : ''}{g.state === 'done' ? 'đã xong' : 'bị lỗi — đã hoàn lượt'}</span>
                                <button type="button" onClick={() => setToasts(list => list.filter(x => x.id !== g.id))} className="text-slate-400 hover:text-white"><X size={14} /></button>
                            </div>
                            {g.state === 'done' && g.urls?.[0] && <a className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-amber-300 hover:underline" href={g.urls[0]} target="_blank" rel="noreferrer" download><Download size={12} /> Tải về ngay</a>}
                        </div>
                    ))}
                </div>
            )}
            {tab === 'create' && <CreateTab api={api} me={me} gens={gens} setGens={setGens} refreshMe={refreshMe} draft={draft} setDraft={setDraft} onLibrary={() => setTab('library')} />}
            {tab === 'library' && <LibraryTab api={api} gens={gens} setGens={setGens} onUse={useGeneration} onFix={fixGeneration} onVideo={videoFromImage} />}
            {tab === 'prompts' && <MyPromptsTab api={api} refreshMe={refreshMe} onUse={id => { setDraft(d => ({ ...d, masterId: `mine:${id}` })); setTab('create'); }} />}
            {tab === 'pages' && <PagesTab api={api} gens={gens} admin={me.role === 'admin'} />}
            {tab === 'trends' && <TrendTab api={api} admin={me.role === 'admin'} onUseIdea={(niche, idea) => { setDraft(d => ({ ...d, niche, idea: idea.title ? `${idea.title}. ${idea.idea}` : idea.idea, title: idea.title || d.title })); setTab('create'); }} />}
            {tab === 'insight' && <InsightTab api={api} admin={me.role === 'admin'} />}
            {tab === 'board' && <BoardTab api={api} onUsePrompt={p => useGeneration({ kind: p.type === 'photo' ? 'image' : 'video', prompt: p.prompt, title: p.title })} />}
            {tab === 'guide' && <GuideTab />}
            {tab === 'admin' && me.role === 'admin' && <AdminTab api={api} refreshMe={refreshMe} />}
        </div>
    );
}

function GuideTab() {
    return (
        <div className="grid gap-4 lg:grid-cols-2">
            {FB_GUIDE.map(section => (
                <section key={section.title} className={card}>
                    <h3 className="mb-2 font-bold text-amber-200">{section.title}</h3>
                    <ul className="list-disc space-y-1.5 pl-5 text-sm text-slate-200">
                        {section.items.map((item, i) => <li key={i}>{item}</li>)}
                    </ul>
                </section>
            ))}
        </div>
    );
}
